import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import {
  Sparkles, CheckCircle2, Circle, Wand2, Save, RefreshCw, ChevronRight,
  Type as TypeIcon, Calculator, Link2, ScrollText, RotateCcw,
  ChevronUp, ChevronDown, GripVertical, Eye, EyeOff,
} from 'lucide-react';
import PdfPreviewPane from './scriptAudit/PdfPreviewPane';
import QuotationLetterEditor from './scriptAudit/QuotationLetterEditor';
import StatusLetterEditor from './scriptAudit/StatusLetterEditor';
import FormattingToolbar from './scriptAudit/FormattingToolbar';
import DraftCompareModal from './scriptAudit/DraftCompareModal';
import { RoofDetailsGridEditor } from '@/components/InstantReportScriptsEditor';

/**
 * Script Audit Studio — sequential cell-by-cell audit workspace for the
 * 3 report variants × 6 roof_keys. Left: live PDF preview. Middle:
 * ordered cell picker with 🔴/🟡/🟢 status. Right: edit form + Claude
 * Sonnet 4.6 [🤖 Suggest 3 alternates] with tone chips (Shorter · Longer
 * · More formal · Friendlier).
 *
 * Every cell change auto-saves. Verify without editing to mark 🟢. Progress
 * tracked in `script_audit_progress` collection on the backend.
 */
// iter303g — Variants are grouped into 3 classes so the Script Audit
// Studio can show a two-tier tab bar (class → variant) and hide the
// roof-type row for classes that don't need it.
//   • appraisal_reports — roof-type-scoped (uses ROOF_KEYS row)
//   • quote_letters     — quote-type-scoped (Manage/Quote Types drives)
//   • status_letters    — status/action-scoped (job workflow)
const CLASSES = [
  { key: 'appraisal_reports', label: 'Appraisal Reports' },
  { key: 'quote_letters',     label: 'Quote Letters'     },
  { key: 'status_letters',    label: 'Status/Action Letters' },
  { key: 'status_messages',   label: 'Status/Action Messages (SMS)' },
];

const VARIANTS = [
  { key: 'instant_free',     class: 'appraisal_reports', label: 'Instant Free',       color: 'bg-slate-500'  },
  { key: 'detailed_single',  class: 'appraisal_reports', label: 'Detailed 1-Comment', color: 'bg-amber-500'  },
  { key: 'detailed_multi',   class: 'appraisal_reports', label: 'Detailed Multi',     color: 'bg-purple-500' },
  // iter298r — Quotation Letter variant. Sourced from a DIFFERENT PDF
  // endpoint (/reports/quotation-detailed) so `pdfPreviewUrl` swaps its
  // path when this variant is active. Editor uses a stacked-section
  // panel (QuotationLetterEditor) instead of the cell picker + editor.
  { key: 'quotation_letter', class: 'quote_letters',     label: 'Quotation Letter',   color: 'bg-emerald-600' },
  // iter299x — Invoice Letter variant. Mirror of Quotation Letter with
  // different wording + only 2 term blocks (Payment Terms, Payment
  // Amount). PDF served from `/reports/invoice-letter`.
  { key: 'invoice_letter',   class: 'quote_letters',     label: 'Invoice Letter',     color: 'bg-orange-600' },
  // iter300a / iter303g — Status/Action Letters. Sent to clients at key
  // job-status transitions. Backend renderer supports 3 today
  // (appraisal_booked, quote_sent, paid). The other 5 tabs render a
  // "awaiting design — provide sample PDF" placeholder card until the
  // user provides Access-DB samples. Ordered to match the natural
  // job-workflow flow: booked → quoted → accepted → in-progress →
  // completed → invoiced → paid → declined.
  { key: 'status_appraisal_booked',       class: 'status_letters',  label: 'Auth Letter',                color: 'bg-sky-600'    },
  // iter303aj — Confirmation Letter (DRAFT). Sent to the client immediately
  // after the Schedule Appraisal task completes. Marked DRAFT until an admin
  // audits + flips is_draft=false on the seeded body.
  { key: 'status_appraisal_confirmation', class: 'status_letters',  label: 'Confirmation Letter',        color: 'bg-emerald-600' },
  { key: 'status_quote_sent',             class: 'status_letters',  label: 'Cover Letter',               color: 'bg-indigo-600' },
  // iter303ak-fix23j · 3 deposit-aware Accepted letter variants
  // (replaces the old status_accepted / _confirmation / _summary trio).
  // Auto-selected at Accepted T20 based on deposit state from T10.
  { key: 'status_accepted_deposit_received',    class: 'status_letters', label: 'Accepted · Deposit Received',    color: 'bg-emerald-500' },
  { key: 'status_accepted_no_deposit_required', class: 'status_letters', label: 'Accepted · No Deposit Required', color: 'bg-teal-500' },
  { key: 'status_accepted_awaiting_deposit',    class: 'status_letters', label: 'Accepted · Awaiting Deposit',    color: 'bg-amber-500' },
  { key: 'status_work_starting_confirmation', class: 'status_letters', label: 'Work in Progress',     color: 'bg-blue-600'   },
  { key: 'status_owner_completion_letter',    class: 'status_letters', label: 'Completed',            color: 'bg-cyan-600'   },
  { key: 'status_work_complete_qi_booked',    class: 'status_letters', label: 'Completed · QI Booked', color: 'bg-cyan-500'  },
  { key: 'status_invoiced_letter',            class: 'status_letters', label: 'Invoiced',             color: 'bg-orange-500' },
  { key: 'status_paid',                   class: 'status_letters',  label: 'Testimonial',                color: 'bg-purple-600' },
  { key: 'status_quote_declined',         class: 'status_letters',  label: 'Declined',                   color: 'bg-rose-600'   },
  // iter303aj — Status/Action Messages (SMS). Short text confirmations
  // sent alongside emailed letters when the client has a mobile on file.
  { key: 'msg_appraisal_confirmation',    class: 'status_messages', label: 'Confirmation SMS',           color: 'bg-purple-500',  isDraft: true },
  // iter303ak-fix22 · Track C v2 · Quote Sent SMS registration so
  // SmsPreview → Studio deep-link lands on a known variant.
  { key: 'msg_quote_sent',                class: 'status_messages', label: 'Quote Sent SMS',             color: 'bg-indigo-500',  isDraft: true },
];

const ROOF_KEYS = [
  'clay_tile', 'color_tile', 'decramastic',
  'longrun_iron', 'modern_concrete', 'old_concrete',
];

const TONE_CHIPS = [
  { key: '',           label: '3 alternates',    hint: 'Default — 3 rewrites, same length' },
  { key: 'shorter',    label: 'Shorter',         hint: 'Trim ~30%, keep every fact' },
  { key: 'longer',     label: 'Longer',          hint: 'Expand ~30% with supporting detail' },
  { key: 'more_formal', label: 'More formal',    hint: 'Business-appointment register' },
  { key: 'friendlier', label: 'Friendlier',      hint: 'Warmer, conversational tone' },
];

// ── Insert-Field palette ─────────────────────────────────────────────
// Grouped list of tokens available for interpolation. Kept in lock-step
// with the resolver in `backend/routes/reports.py` (`_replace_vars`) —
// any addition/removal there MUST be mirrored here (and vice-versa).
// Grouping surfaces context (Roof Details vs Client vs Estimates) so a
// 20-chip row doesn't collapse into visual soup.
const INSERT_FIELD_GROUPS = [
  {
    label: 'Roof Details',
    tokens: [
      { key: 'description', label: 'Description' },
      { key: 'type',        label: 'Roof Type'  },
      { key: 'state',       label: 'State'      },
      { key: 'brand',       label: 'Brand'      },
      { key: 'profile',     label: 'Profile'    },
      { key: 'pitch',       label: 'Pitch'      },
      { key: 'colour',      label: 'Colour'     },
      { key: 'growth',      label: 'Growth'     },
      { key: 'surface',     label: 'Surface'    },
      { key: 'floor_m2',         label: 'Floor M²'          },
      { key: 'stories',          label: 'Stories'           },
      { key: 'age',              label: 'Age'               },
      // iter298b — ColourTile / Decramastic-specific lengths.
      { key: 'ridge_length',     label: 'Ridge length (m)'  },
      { key: 'perimeter_length', label: 'Perimeter (m)'     },
      { key: 'barge_length',     label: 'Barge length (m)'  },
    ],
  },
  {
    label: 'Client / Property',
    tokens: [
      { key: 'client_name',      label: 'Client Name'      },
      { key: 'property_address', label: 'Property Address' },
    ],
  },
  {
    label: 'Estimates',
    tokens: [
      { key: 'rescrew_estimation',            label: 'ReScrew Estimation'       },
      { key: 'repoint_estimate',              label: 'Repoint Estimate'         },
      { key: 'bed_and_point_estimate',        label: 'Bed and Point Estimate'   },
      { key: 'extra_work_estimate',           label: 'Extra Work Estimate'      },
      { key: 'restoration_estimation',        label: 'Restoration Estimation'   },
      { key: 'reroof_estimation',             label: 'Re-Roof Estimation'       },
      // iter298b — ColourTile / Decramastic split. `colortile_repair_estimation`
      // is a friendly alias of `rescrew_estimation` (sum of the 3 sub-rows);
      // the sub-tokens surface each line individually for scripts that want
      // to break the total out.
      { key: 'colortile_repair_estimation',   label: 'ColourTile Repair Total'  },
      { key: 'rescrew_bottom_course_estimate',label: 'Bottom Course $'          },
      { key: 'rescrew_ridging_estimate',      label: 'Ridging $'                },
      { key: 'rescrew_barging_estimate',      label: 'Barging $'                },
    ],
  },
];

const STATUS_META = {
  pending:  { color: 'text-slate-400',  bg: 'bg-slate-100', icon: Circle,        label: 'Not audited' },
  verified: { color: 'text-emerald-600', bg: 'bg-emerald-100', icon: CheckCircle2, label: 'Verified' },
  edited:   { color: 'text-amber-600',  bg: 'bg-amber-100',  icon: Sparkles,     label: 'Edited' },
};

// Human-readable section labels for the reorderable "Report Body" tab.
// Keys mirror `INSTANT_REPORT_SECTION_LABELS` in `backend/routes/reports.py`;
// unknown keys fall through to a Title-Cased derivation of the section_key.
const SECTION_LABELS = {
  title:                       'Title',
  intro:                       'Intro Paragraph',
  roof_details:                'Roof Details',
  conditions:                  'Per-area Conditions',
  per_area_defects:            'Per-area Defects',
  repair_options:              'Repair Options',
  ridging_info:                'Ridging Info',
  roofing_information:         'Roofing Information',
  roof_condition:              'Roof Condition',
  appraisal_narrative:         'Appraisal Cover Narrative',
  roof_requirements:           'Roof Requirements',
  extra_works:                 'Extra Works',
  repair_estimate_comment:     'Repair Estimate Comment',
  rescrew_estimate:            'ReScrew Estimation ($)',
  repoint_estimate:            'Repoint Estimate ($)',
  bedpoint_estimate:           'Bed and Point Estimate ($)',
  extra_work_estimate:         'Extra Work Estimate ($)',
  recoat_restoration_comment:  'Recoat / Restoration Comment',
  restoration_estimate:        'Restoration Estimation ($)',
  restoration_notes:           'Restoration Notes',
  reroof_notes:                'Reroof Notes',
  reroof_estimate:             'Reroof Estimation ($)',
  illustration:                'Illustration',
  vent_note:                   'Vent Note',
  defect_photo_gallery:        'Defect Photo Gallery',
  closing:                     'Closing Paragraphs',
};

// Small stateless helper — kept module-scoped to avoid React
// re-mounting the icon on every parent render (react/no-unstable-nested-components).
function StatusIcon({ status, size = 12 }) {
  const meta = STATUS_META[status] || STATUS_META.pending;
  const Icon = meta.icon;
  return <Icon size={size} className={meta.color} strokeWidth={2.5} />;
}

// Matrix grid — rows: cells, cols: 6 roof_keys for the current variant.
// Column-op = click roof header → verify every cell in that roof.
// Row-op   = click row label   → verify this cell across all 6 roofs.
function MatrixGrid({ data, onBulkVerify, onCellClick }) {
  if (!data) return <div className="text-xs text-slate-400 italic p-3">Loading matrix…</div>;
  const { roof_keys: roofKeys, cells } = data;
  return (
    <div className="overflow-x-auto" data-testid="matrix-grid">
      <table className="text-[10px] w-full">
        <thead className="sticky top-0 bg-white">
          <tr>
            <th className="text-left px-1 py-1 font-bold text-slate-500 uppercase tracking-wider">Cell</th>
            {roofKeys.map(rk => (
              <th key={rk} className="text-center px-1 py-1">
                <button data-testid={`matrix-col-verify-${rk}`} onClick={() => window.confirm(`Verify every cell in ${rk.replace(/_/g, ' ')}?`) && onBulkVerify({ roof_key: rk })} className="text-[9px] font-bold uppercase tracking-wider text-slate-500 hover:text-emerald-700" title={`Bulk-verify all cells in ${rk}`}>{rk.replace(/_/g, ' ')} ✓</button>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {cells.map(row => (
            <tr key={`${row.cell_type}::${row.cell_key}`} className="border-t border-slate-100">
              <td className="px-1 py-0.5 text-slate-700 font-mono truncate max-w-[140px]">
                <button data-testid={`matrix-row-verify-${row.cell_key}`} onClick={() => window.confirm(`Verify "${row.label}" across ALL 6 roofs?`) && onBulkVerify({ cell_type: row.cell_type, cell_key: row.cell_key })} className="hover:text-emerald-700 truncate" title={`Bulk-verify "${row.label}" across all roofs`}>{row.label}</button>
              </td>
              {roofKeys.map(rk => {
                const s = row.row_status[rk] || 'pending';
                const meta = STATUS_META[s] || STATUS_META.pending;
                return (
                  <td key={rk} className="text-center px-1 py-0.5">
                    <button
                      data-testid={`matrix-cell-${row.cell_key}-${rk}`}
                      onClick={() => onCellClick(row.cell_type, row.cell_key, rk)}
                      className={`w-5 h-5 rounded-full ${meta.bg} border border-slate-200 hover:scale-110 transition-transform`}
                      title={`${row.label} · ${rk} · ${s}`}
                    />
                  </td>
                );
              })}
            </tr>
          ))}
          {cells.length === 0 && <tr><td colSpan={roofKeys.length + 1} className="text-center py-4 text-slate-400 italic">No audited cells yet for this variant.</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

export default function ScriptAuditPage() {
  const { api } = useAuth();
  // Restore last-used variant / roof from localStorage so the Studio
  // opens where the operator left off. Falls back to sensible defaults.
  const _savedNav = (() => {
    try { return JSON.parse(localStorage.getItem('script-audit-last-nav') || '{}'); }
    catch { return {}; }
  })();
  const [variant, setVariant] = useState(_savedNav.variant || 'detailed_multi');
  // iter303g — activeClass derives from the current variant. Persists
  // separately to localStorage so class-only clicks remember position.
  const [activeClass, setActiveClass] = useState(() => {
    const savedClass = _savedNav.active_class;
    if (savedClass && CLASSES.some(c => c.key === savedClass)) return savedClass;
    return (VARIANTS.find(v => v.key === (_savedNav.variant || 'detailed_multi'))?.class) || 'appraisal_reports';
  });
  const [roofKey, setRoofKey] = useState(_savedNav.roof_key || 'longrun_iron');
  const [pickerTab, setPickerTab] = useState(_savedNav.picker_tab || 'report_body');   // 'report_body' | 'roof_details' | 'defects'
  const [cells, setCells] = useState([]);
  // Section reorder state (iter295) — variant-scoped PDF section order.
  const [sectionOrder, setSectionOrder] = useState([]);
  const [defaultSectionOrder, setDefaultSectionOrder] = useState([]);
  const [reorderableSectionKeys, setReorderableSectionKeys] = useState([]);
  const [dragSectionKey, setDragSectionKey] = useState(null);   // for drag-drop rows

  // iter296 · Illustration layout + estimate-row widths — variant-scoped
  // template design controls. Populated from /script-audit/cells response
  // (backend embeds them on the returned template metadata).
  const [illustrationLayouts, setIllustrationLayouts] = useState([]);
  const [illustrationLayout, setIllustrationLayout] = useState('standalone');
  const [estRowWidths, setEstRowWidths] = useState({ label_mm: 55, gap_mm: 8, value_mm: 35 });
  const [showLayoutPanel, setShowLayoutPanel] = useState(false);
  const [showCustomLayoutModal, setShowCustomLayoutModal] = useState(false);
  const [progress, setProgress] = useState({ total: 0, verified: 0, edited: 0, pending: 0 });
  const [activeIdx, setActiveIdx] = useState(0);
  const _pendingCellKey = useRef(_savedNav.cell_key || null);   // restored cell_key to re-select once cells load
  const [filterUnaudited, setFilterUnaudited] = useState(false);
  const [refAppraisalId, setRefAppraisalId] = useState(null);
  const [pdfBust, setPdfBust] = useState(0); // increments to force iframe reload
  // Status letters with a PDF layout — driven by the backend.
  const [supportedStatusKeys, setSupportedStatusKeys] = useState(['appraisal_booked', 'quote_sent', 'paid']);
  useEffect(() => {
    api.get('/app-settings/status-letters-meta')
      .then(r => setSupportedStatusKeys(r.data?.supported || []))
      .catch(() => {});
  }, [api]);

  // Edit-panel state
  const [draft, setDraft] = useState('');
  const textareaRef = useRef(null);   // for cursor-position aware token insert
  const [rateDraft, setRateDraft] = useState({});
  const [saveState, setSaveState] = useState('idle'); // idle | saving | saved
  const [aiBusy, setAiBusy] = useState(false);
  const [aiAlternates, setAiAlternates] = useState([]);
  const [aiTone, setAiTone] = useState('');
  const [linkedReports, setLinkedReports] = useState([]);
  const [selectedLinks, setSelectedLinks] = useState(new Set());   // "variant::roof_key" pairs
  // ── Iter288 3B ────────────────────────────────────────────────────────
  // Undo/redo stack (10-deep). Push a snapshot on every edit; pop on Undo.
  const [undoStack, setUndoStack] = useState([]);   // [{draft, rateDraft}]
  const [redoStack, setRedoStack] = useState([]);
  const [showCopyTools, setShowCopyTools] = useState(false);
  const [copySources, setCopySources] = useState([]);
  const [copySearch, setCopySearch] = useState('');
  // iter297i · Copy From — default filter to SAME cell_type + cell_key
  // across other roof_keys of the SAME variant. Advanced toggle unlocks
  // the full cross-cell / cross-variant list.
  const [copyAdvanced, setCopyAdvanced] = useState(false);
  const [copyBusy, setCopyBusy] = useState(false);
  // Roof-type ticks for condition_script cells (mini-Matrix).
  const [scriptRoofTypes, setScriptRoofTypes] = useState([]);
  // ── Iter288 3C ─────────────────────────────────────────────────────
  const [viewMode, setViewMode] = useState('sequential');  // 'sequential' | 'matrix'
  const [matrixData, setMatrixData] = useState(null);
  const [suggestBusy, setSuggestBusy] = useState(false);
  const [suggestions, setSuggestions] = useState([]);      // similar-source hits
  const [showFocusedPreview, setShowFocusedPreview] = useState(false);

  // ── Phase B · Rich formatting ─────────────────────────────────────
  const [fonts, setFonts] = useState([]);          // catalogue from /script-audit/fonts
  const [formattingMap, setFormattingMap] = useState({});   // { "cell_type::cell_key": {...} }
  const [multiSelectMode, setMultiSelectMode] = useState(false);
  const [selectedCells, setSelectedCells] = useState(new Set());   // keys of "cell_type::cell_key"
  const [roofDetailFieldMeta, setRoofDetailFieldMeta] = useState({});  // {key: {label}} for the grid editor

  // iter297d · Design Draft Library — save/apply layout snapshots per variant
  const [designDrafts, setDesignDrafts] = useState([]);
  const [showDraftsMenu, setShowDraftsMenu] = useState(false);
  const [draftBusy, setDraftBusy] = useState(false);
  // iter297e · Edit-Draft mode — when set, Studio edits accumulate in an
  // in-memory buffer instead of persisting to the underlying template.
  // Save button PUTs the buffer to the draft; Discard reverts to the last
  // server state; Exit returns to Template-editing mode.
  const [editingDraftId, setEditingDraftId] = useState(null);
  const [editingDraftName, setEditingDraftName] = useState('');
  const [draftDirty, setDraftDirty] = useState(false);
  const [draftSaving, setDraftSaving] = useState(false);
  const isEditingDraft = !!editingDraftId;

  // iter298d · Draft Compare modal — set of two draft ids to show side by
  // side. Only opened via the "🆚 Compare" button in the Design Drafts
  // dropdown, and only enabled when there are ≥2 drafts for the current
  // variant.
  const [showCompareModal, setShowCompareModal] = useState(false);

  // ── Data fetches ─────────────────────────────────────────────────────
  const loadCells = useCallback(async () => {
    // iter299z + iter300a — Letter variants (`quotation_letter`,
    // `invoice_letter`, `status_*`) don't have audit "cells" — they
    // render via QuotationLetterEditor or the Status Letter placeholder.
    // Short-circuit so the backend cells endpoint isn't hit (used to
    // 400 with "unknown variant" → alert popup on every tab switch).
    if (variant === 'quotation_letter' || variant === 'invoice_letter' || (variant || '').startsWith('status_')) {
      setCells([]);
      setSectionOrder([]);
      setDefaultSectionOrder([]);
      setReorderableSectionKeys([]);
      setProgress({ total: 0, verified: 0, edited: 0, pending: 0 });
      setActiveIdx(0);
      return;
    }
    try {
      const { data } = await api.get('/script-audit/cells', {
        params: { variant, roof_key: roofKey },
      });
      const cellsResp = data.cells || [];
      setCells(cellsResp);
      setSectionOrder(data.section_order || []);
      setDefaultSectionOrder(data.default_section_order || []);
      setReorderableSectionKeys(data.reorderable_section_keys || []);
      setIllustrationLayout(data.illustration_layout || 'standalone');
      setEstRowWidths(data.estimate_row_widths || { label_mm: 55, gap_mm: 8, value_mm: 35 });
      setProgress(data.progress || { total: 0, verified: 0, edited: 0, pending: 0 });
      // Prefer the stashed cell_key from localStorage on first load; fall
      // back to cell #0. Clear the pending ref after use so subsequent
      // (variant × roof) switches reset to the top as before.
      const pending = _pendingCellKey.current;
      if (pending) {
        const idx = cellsResp.findIndex(c => c.cell_key === pending);
        setActiveIdx(idx >= 0 ? idx : 0);
        _pendingCellKey.current = null;
      } else {
        setActiveIdx(0);
      }
    } catch (e) {
      alert('Failed to load audit cells: ' + (e?.response?.data?.detail || e.message));
    }
  }, [api, variant, roofKey]);

  useEffect(() => { loadCells(); }, [loadCells]);

  // Persist last-used (variant, roof_key, cell_key, picker_tab) so a
  // page refresh reopens the Studio exactly where the operator left off.
  useEffect(() => {
    const currentCellKey = cells[activeIdx]?.cell_key || '';
    try {
      localStorage.setItem('script-audit-last-nav', JSON.stringify({
        variant, active_class: activeClass, roof_key: roofKey, cell_key: currentCellKey, picker_tab: pickerTab,
      }));
    } catch { /* localStorage full — silently skip */ }
  }, [variant, activeClass, roofKey, activeIdx, cells, pickerTab]);

  // Load Google Fonts substitutes once so the picker previews render in
  // the correct family. Native Microsoft fonts (Windows/Office machines)
  // gracefully fall back via the CSS stack in FormattingToolbar._cssFamilyFor.
  useEffect(() => {
    const id = 'script-audit-google-fonts';
    if (document.getElementById(id)) return;
    const link = document.createElement('link');
    link.id = id;
    link.rel = 'stylesheet';
    link.href = 'https://fonts.googleapis.com/css2?family=Tinos:ital,wght@0,400;0,700;1,400;1,700&family=Arimo:ital,wght@0,400;1,400&family=Carlito:ital,wght@0,400;0,700;1,400;1,700&family=Comic+Neue:ital,wght@0,400;0,700;1,400&family=Roboto+Slab:wght@400&family=Copse&display=swap';
    document.head.appendChild(link);
  }, []);

  // ── Font catalogue (loaded once) ────────────────────────────────
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get('/script-audit/fonts');
        setFonts(data.fonts || []);
      } catch { setFonts([]); }
    })();
  }, [api]);

  // ── Roof Detail field labels (loaded once) for the grid editor ──
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get('/instant-report-sections');
        setRoofDetailFieldMeta(data.roof_detail_fields || {});
      } catch { setRoofDetailFieldMeta({}); }
    })();
  }, [api]);

  // ── Per (variant, roof_key) formatting map ─────────────────────
  const loadFormatting = useCallback(async () => {
    try {
      const { data } = await api.get('/script-audit/formatting', { params: { variant, roof_key: roofKey } });
      setFormattingMap(data.formatting || {});
    } catch { setFormattingMap({}); }
  }, [api, variant, roofKey]);
  useEffect(() => { loadFormatting(); }, [loadFormatting]);
  // Clear selection when the visible cell set changes (variant / roof switch).
  useEffect(() => { setSelectedCells(new Set()); }, [variant, roofKey]);

  // ── iter297d · Design Draft library ─────────────────────────────
  const loadDrafts = useCallback(async () => {
    try {
      const { data } = await api.get('/script-audit/drafts', { params: { variant } });
      setDesignDrafts(data.drafts || []);
    } catch { setDesignDrafts([]); }
  }, [api, variant]);
  useEffect(() => { loadDrafts(); }, [loadDrafts]);

  const saveCurrentAsDraft = useCallback(async () => {
    const name = window.prompt(
      `Save the current ${variant.replace(/_/g, ' ')} · ${roofKey.replace(/_/g, ' ')} design as a reusable draft?\n\nDraft name:`,
      `${roofKey.replace(/_/g, ' ')} — ${new Date().toISOString().slice(0, 10)}`
    );
    if (!name || !name.trim()) return;
    setDraftBusy(true);
    try {
      await api.post('/script-audit/drafts', { name: name.trim(), variant, roof_key: roofKey });
      await loadDrafts();
      setShowDraftsMenu(true);
    } catch (e) {
      alert('Failed to save draft: ' + (e?.response?.data?.detail || e.message));
    }
    setDraftBusy(false);
  }, [api, variant, roofKey, loadDrafts]);

  const applyDraft = useCallback(async (draft) => {
    const target = window.prompt(
      `Apply "${draft.name}" (from ${draft.source_roof_key.replace(/_/g, ' ')}) to which roof_type?\n\nAvailable: ${ROOF_KEYS.join(', ')}\n\nApplying will overwrite the target's section order, section flags, illustration layout, estimate row widths/titles, and all cell formatting for the SAME variant.`,
      roofKey
    );
    if (!target || !target.trim()) return;
    const targetRk = target.trim();
    if (!ROOF_KEYS.includes(targetRk)) {
      alert(`Invalid roof_type "${targetRk}". Must be one of: ${ROOF_KEYS.join(', ')}`);
      return;
    }
    if (!window.confirm(`Overwrite ${variant.replace(/_/g, ' ')} · ${targetRk.replace(/_/g, ' ')} with draft "${draft.name}"? This replaces all layout & formatting on the target.`)) return;
    setDraftBusy(true);
    try {
      const { data } = await api.post(`/script-audit/drafts/${draft._id}/apply`, { target_roof_key: targetRk });
      alert(`Applied — ${data.design_fields_applied} design fields + ${data.formatting_rows_applied} formatting rows.`);
      if (targetRk === roofKey) {
        // Reload current view so operator sees the applied design
        await loadCells();
        await loadFormatting();
        setPdfBust(b => b + 1);
      }
      setShowDraftsMenu(false);
    } catch (e) {
      alert('Failed to apply draft: ' + (e?.response?.data?.detail || e.message));
    }
    setDraftBusy(false);
  }, [api, variant, roofKey, loadCells, loadFormatting]);

  const renameDraft = useCallback(async (draft) => {
    const name = window.prompt('Rename draft:', draft.name);
    if (!name || !name.trim() || name.trim() === draft.name) return;
    try {
      await api.put(`/script-audit/drafts/${draft._id}`, { name: name.trim() });
      await loadDrafts();
    } catch (e) {
      alert('Rename failed: ' + (e?.response?.data?.detail || e.message));
    }
  }, [api, loadDrafts]);

  const deleteDraft = useCallback(async (draft) => {
    if (!window.confirm(`Delete draft "${draft.name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/script-audit/drafts/${draft._id}`);
      await loadDrafts();
    } catch (e) {
      alert('Delete failed: ' + (e?.response?.data?.detail || e.message));
    }
  }, [api, loadDrafts]);

  // ── iter297e · Edit-Draft mode ─────────────────────────────────
  // Loading a draft REPLACES the studio's layout state with the draft's
  // design so the operator sees what's persisted. Edits then accumulate
  // in that same state (via intercept guards on each layout PUT) and
  // are committed to the draft only on Save.
  const enterDraftEdit = useCallback(async (draft) => {
    if (draftDirty && !window.confirm('You have unsaved draft edits. Switch drafts and lose them?')) return;
    setDraftSaving(true);
    try {
      const { data } = await api.get(`/script-audit/drafts/${draft._id}`);
      const d = data.draft || {};
      const design = d.design || {};
      // Hydrate the layout state so Studio reflects the draft's design.
      if (design.section_order) setSectionOrder(design.section_order);
      if (design.illustration_layout) setIllustrationLayout(design.illustration_layout);
      if (design.estimate_row_widths) setEstRowWidths(design.estimate_row_widths);
      // Overlay draft's section_flags onto the cells' include_in_pdf so
      // the picker's include checkboxes reflect the draft's state.
      if (design.section_flags) {
        setCells(prev => prev.map(c => (
          c.cell_type === 'template_field' && design.section_flags[c.cell_key] !== undefined
            ? { ...c, include_in_pdf: !!design.section_flags[c.cell_key] }
            : c
        )));
      }
      // Rebuild formatting map from draft's cell_formatting rows scoped
      // to current roof_key (roof-agnostic rows apply to all).
      const draftFmt = d.cell_formatting || [];
      const fmap = {};
      draftFmt.forEach(f => {
        const rk = f.roof_key;
        if (rk && rk !== roofKey) return;
        const k = `${f.cell_type}::${f.cell_key}`;
        fmap[k] = {
          font_family: f.font_family, font_size: f.font_size, color: f.color,
          bold: f.bold || false, italic: f.italic || false, underline: f.underline || false,
          bullet_char: f.bullet_char,
        };
      });
      setFormattingMap(fmap);
      setEditingDraftId(draft._id);
      setEditingDraftName(draft.name);
      setDraftDirty(false);
      setShowDraftsMenu(false);
      // Refresh PDF preview to render with this draft active
      setPdfBust(b => b + 1);
    } catch (e) {
      alert('Load draft failed: ' + (e?.response?.data?.detail || e.message));
    }
    setDraftSaving(false);
  }, [api, roofKey, draftDirty]);

  const exitDraftEdit = useCallback(async () => {
    if (draftDirty && !window.confirm('You have unsaved draft edits. Exit and lose them?')) return;
    setEditingDraftId(null);
    setEditingDraftName('');
    setDraftDirty(false);
    // Re-load the raw template state from server
    await loadCells();
    await loadFormatting();
    setPdfBust(b => b + 1);
  }, [draftDirty, loadCells, loadFormatting]);

  const discardDraftEdits = useCallback(async () => {
    if (!editingDraftId) return;
    if (draftDirty && !window.confirm('Discard your unsaved edits and reload draft from server?')) return;
    try {
      const { data } = await api.get(`/script-audit/drafts/${editingDraftId}`);
      const d = data.draft || {};
      const design = d.design || {};
      if (design.section_order) setSectionOrder(design.section_order);
      if (design.illustration_layout) setIllustrationLayout(design.illustration_layout);
      if (design.estimate_row_widths) setEstRowWidths(design.estimate_row_widths);
      if (design.section_flags) {
        setCells(prev => prev.map(c => (
          c.cell_type === 'template_field' && design.section_flags[c.cell_key] !== undefined
            ? { ...c, include_in_pdf: !!design.section_flags[c.cell_key] }
            : c
        )));
      }
      const draftFmt = d.cell_formatting || [];
      const fmap = {};
      draftFmt.forEach(f => {
        const rk = f.roof_key;
        if (rk && rk !== roofKey) return;
        const k = `${f.cell_type}::${f.cell_key}`;
        fmap[k] = {
          font_family: f.font_family, font_size: f.font_size, color: f.color,
          bold: f.bold || false, italic: f.italic || false, underline: f.underline || false,
          bullet_char: f.bullet_char,
        };
      });
      setFormattingMap(fmap);
      setDraftDirty(false);
      setPdfBust(b => b + 1);
    } catch (e) {
      alert('Discard failed: ' + (e?.response?.data?.detail || e.message));
    }
  }, [api, editingDraftId, roofKey, draftDirty]);

  const saveDraftEdits = useCallback(async () => {
    if (!editingDraftId) return;
    setDraftSaving(true);
    try {
      // Derive section_flags from current cells' include_in_pdf state.
      const section_flags = {};
      cells.forEach(c => {
        if (c.cell_type === 'template_field' && c.cell_key !== 'roof_details_fields') {
          section_flags[c.cell_key] = c.include_in_pdf !== false;
        }
      });
      // Rebuild cell_formatting rows from the current formattingMap.
      const rows = Object.entries(formattingMap).map(([k, v]) => {
        const [cell_type, cell_key] = k.split('::');
        return {
          cell_type, cell_key,
          variant, roof_key: roofKey,
          font_family: v.font_family, font_size: v.font_size, color: v.color,
          bold: !!v.bold, italic: !!v.italic, underline: !!v.underline,
          bullet_char: v.bullet_char,
        };
      });
      await api.put(`/script-audit/drafts/${editingDraftId}`, {
        design: {
          section_order: sectionOrder,
          section_flags,
          illustration_layout: illustrationLayout,
          estimate_row_widths: estRowWidths,
        },
        cell_formatting: rows,
      });
      setDraftDirty(false);
      await loadDrafts();
      setPdfBust(b => b + 1);
    } catch (e) {
      alert('Save failed: ' + (e?.response?.data?.detail || e.message));
    }
    setDraftSaving(false);
  }, [api, editingDraftId, sectionOrder, illustrationLayout, estRowWidths, formattingMap, variant, roofKey, cells, loadDrafts]);

  // iter297d Phase 2 — Toggle a draft as the variant's default.
  // Only ONE default per variant; setting one clears siblings.
  // PDF render endpoint automatically applies the default draft's design.
  const setDraftDefault = useCallback(async (draft) => {
    setDraftBusy(true);
    try {
      if (draft.is_default) {
        await api.post(`/script-audit/drafts/${draft._id}/clear-default`);
      } else {
        await api.post(`/script-audit/drafts/${draft._id}/set-default`);
      }
      await loadDrafts();
      setPdfBust(b => b + 1);
    } catch (e) {
      alert('Failed to update default: ' + (e?.response?.data?.detail || e.message));
    }
    setDraftBusy(false);
  }, [api, loadDrafts]);

  // iter297d Phase 2 — "Save current layout to this draft".
  // Captures the current (variant, roof_key) template design + formatting
  // and PUTs it to the draft. Use after making edits in Studio to keep
  // a draft in sync with the current layout.
  const saveCurrentToDraft = useCallback(async (draft) => {
    if (!window.confirm(`Save the CURRENT layout of ${variant.replace(/_/g, ' ')} · ${roofKey.replace(/_/g, ' ')} to draft "${draft.name}"?\n\nThis overwrites the draft's design + formatting with what you see in Studio right now.`)) return;
    setDraftBusy(true);
    try {
      // Fetch the current template's design fields (server is source of truth)
      const { data: tpl } = await api.get('/script-audit/template-design', { params: { variant, roof_key: roofKey } });
      const { data: fmt } = await api.get('/script-audit/formatting-rows', { params: { variant, roof_key: roofKey } });
      await api.put(`/script-audit/drafts/${draft._id}`, {
        design: tpl.design || {},
        cell_formatting: fmt.rows || [],
      });
      await loadDrafts();
      setPdfBust(b => b + 1);
      alert(`Draft "${draft.name}" updated with current ${roofKey.replace(/_/g, ' ')} layout.`);
    } catch (e) {
      alert('Save failed: ' + (e?.response?.data?.detail || e.message));
    }
    setDraftBusy(false);
  }, [api, variant, roofKey, loadDrafts]);

  // Auto-pick most-recently-updated appraisal for the left-pane preview
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get('/appraisals', { params: { limit: 1 } });
        const items = Array.isArray(data) ? data : (data.items || []);
        const first = items[0];
        setRefAppraisalId(first?._id || first?.id || null);
      } catch {
        setRefAppraisalId(null);
      }
    })();
  }, [api]);

  // iter298o — Auto-refresh the PDF preview whenever the browser tab
  // regains focus. Fixes the "I saved on the Roof Appraisal tab but
  // Studio still shows the old PDF" complaint — pdfBust drives the
  // preview URL's cache-buster query param, so bumping it forces the
  // iframe to refetch the PDF from the server. Also runs a lightweight
  // updated_at poll every 20s while visible so preview stays fresh
  // even without a manual tab switch (e.g. two-monitor setups).
  useEffect(() => {
    if (!refAppraisalId) return;
    let lastKnownUpdate = null;
    const bumpIfChanged = async () => {
      try {
        const { data } = await api.get(`/appraisals/${refAppraisalId}`);
        const ua = data?.updated_at || data?.last_saved_at || null;
        if (ua && ua !== lastKnownUpdate) {
          if (lastKnownUpdate !== null) setPdfBust(b => b + 1);
          lastKnownUpdate = ua;
        }
      } catch { /* ignore transient errors */ }
    };
    const onVisible = () => { if (!document.hidden) bumpIfChanged(); };
    document.addEventListener('visibilitychange', onVisible);
    // Warm-up + interval polling. Poll only when the tab is visible so
    // background tabs don't hammer the API.
    bumpIfChanged();
    const poll = setInterval(() => { if (!document.hidden) bumpIfChanged(); }, 20000);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      clearInterval(poll);
    };
  }, [api, refAppraisalId]);


  // Fetch linked reports whenever the active cell changes.
  useEffect(() => {
    const cell = cells[activeIdx];
    if (!cell) { setLinkedReports([]); setSelectedLinks(new Set()); return; }
    setSelectedLinks(new Set());   // fresh cell → fresh selection
    (async () => {
      try {
        const { data } = await api.get('/script-audit/links', {
          params: { cell_type: cell.cell_type, cell_key: cell.cell_key },
        });
        setLinkedReports(data.links || []);
      } catch {
        setLinkedReports([]);
      }
    })();
  }, [api, cells, activeIdx]);

  // Sync draft when active cell changes.
  useEffect(() => {
    const cell = cells[activeIdx];
    if (!cell) return;
    setAiAlternates([]);
    setSaveState('idle');
    setUndoStack([]); setRedoStack([]);
    if (cell.cell_type === 'template_field') {
      // Arrays (roof_details_fields) never populate the textarea; keep
      // draft empty so no stray "[Array]" string ends up in the PDF via
      // a stray save.
      setDraft(typeof cell.value === 'string' ? cell.value : '');
      setRateDraft({});
      setScriptRoofTypes([]);
    } else if (cell.cell_type === 'condition_script') {
      setDraft(cell.value || '');
      setRateDraft({});
      setScriptRoofTypes(cell.roof_types || []);
    } else {
      setRateDraft(cell.value || {});
      setDraft('');
      setScriptRoofTypes([]);
    }
  }, [cells, activeIdx]);

  // ── Undo/Redo helpers ──────────────────────────────────────────────
  const snapshotForUndo = useCallback(() => {
    setUndoStack(prev => {
      const next = [...prev, { draft, rateDraft, scriptRoofTypes }];
      // 10-deep cap — trim from the head, not the tail.
      return next.length > 10 ? next.slice(next.length - 10) : next;
    });
    setRedoStack([]);   // any new edit invalidates the redo path
  }, [draft, rateDraft, scriptRoofTypes]);

  const applyUndo = () => {
    if (undoStack.length === 0) return;
    const top = undoStack[undoStack.length - 1];
    setRedoStack(prev => [...prev, { draft, rateDraft, scriptRoofTypes }]);
    setUndoStack(prev => prev.slice(0, -1));
    setDraft(top.draft || '');
    setRateDraft(top.rateDraft || {});
    setScriptRoofTypes(top.scriptRoofTypes || []);
  };
  const applyRedo = () => {
    if (redoStack.length === 0) return;
    const top = redoStack[redoStack.length - 1];
    setUndoStack(prev => [...prev, { draft, rateDraft, scriptRoofTypes }]);
    setRedoStack(prev => prev.slice(0, -1));
    setDraft(top.draft || '');
    setRateDraft(top.rateDraft || {});
    setScriptRoofTypes(top.scriptRoofTypes || []);
  };

  // Classify each cell into one of the 3 picker tabs. Roof Details grid
  // sits alone under its own tab; defect scripts get their own tab too;
  // everything else lands under Report Body. This keeps the middle
  // column scannable when detailed variants surface ~140 defect cells.
  const cellTab = (c) => {
    if (!c) return 'report_body';
    if (c.cell_type === 'template_field' && c.cell_key === 'roof_details_fields') return 'roof_details';
    if (c.cell_type === 'condition_script') return 'defects';
    return 'report_body';
  };

  // Cells-per-tab counts feed the tab-button badges.
  const tabCounts = useMemo(() => {
    const c = { report_body: 0, roof_details: 0, defects: 0 };
    cells.forEach(cell => { c[cellTab(cell)] = (c[cellTab(cell)] || 0) + 1; });
    return c;
  }, [cells]);

  const visibleCells = useMemo(() => {
    const filtered = filterUnaudited ? cells.filter(c => c.status === 'pending') : cells;
    return filtered.filter(c => cellTab(c) === pickerTab);
  }, [cells, filterUnaudited, pickerTab]);

  const activeCell = cells[activeIdx] || null;

  // Auto-switch to whichever tab contains the active cell so a Next 🔴 /
  // linked-chip / matrix-jump never leaves the operator on a tab where
  // their selected cell is invisible.
  useEffect(() => {
    if (!activeCell) return;
    const t = cellTab(activeCell);
    if (t !== pickerTab) setPickerTab(t);
  }, [activeIdx, cells, activeCell, pickerTab]);

  // ── Actions ──────────────────────────────────────────────────────────
  const patchStatusLocal = (idx, status) => {
    setCells(prev => {
      const next = [...prev];
      if (next[idx]) next[idx] = { ...next[idx], status };
      // Recompute progress.
      const total = next.length;
      const verified = next.filter(c => c.status === 'verified').length;
      const edited = next.filter(c => c.status === 'edited').length;
      setProgress({ total, verified, edited, pending: total - verified - edited });
      return next;
    });
  };

  const saveCell = useCallback(async () => {
    if (!activeCell) return;
    // `roof_details_fields` uses a dedicated grid save path (auto-saves
    // on every drop). Ignore Save-button clicks here so a stale empty
    // `draft` never overwrites the array.
    if (activeCell.cell_type === 'template_field' && activeCell.cell_key === 'roof_details_fields') return;
    setSaveState('saving');
    try {
      let valuePayload;
      if (activeCell.cell_type === 'template_field') {
        // `roof_details_fields` sends an array (grid ordering) instead of
        // a string — grid saves happen via `saveRoofDetailsOrder` below,
        // never here, so a string `draft` is always the right shape.
        valuePayload = draft;
      } else if (activeCell.cell_type === 'condition_script') {
        // Send both body + roof_types so mini-Matrix ticks persist alongside text.
        valuePayload = { body: draft, roof_types: scriptRoofTypes };
      } else {
        valuePayload = rateDraft;
      }
      const body = {
        variant, roof_key: roofKey,
        cell_type: activeCell.cell_type, cell_key: activeCell.cell_key,
        value: valuePayload,
      };
      await api.put('/script-audit/cell', body);
      patchStatusLocal(activeIdx, 'edited');
      // Update the cached cell so switching away and back shows the just-saved text.
      setCells(prev => {
        const n = [...prev];
        if (n[activeIdx]) {
          if (activeCell.cell_type === 'condition_script') {
            n[activeIdx] = { ...n[activeIdx], value: draft, roof_types: scriptRoofTypes };
          } else {
            n[activeIdx] = { ...n[activeIdx], value: valuePayload };
          }
        }
        return n;
      });
      setSaveState('saved');
      setPdfBust(b => b + 1);
      setTimeout(() => setSaveState('idle'), 1500);
    } catch (e) {
      setSaveState('idle');
      alert('Save failed: ' + (e?.response?.data?.detail || e.message));
    }
  }, [api, activeCell, activeIdx, variant, roofKey, draft, rateDraft, scriptRoofTypes]);

  // iter298 · Manual "Refresh Preview" — auto-saves any pending edit in
  // the active cell FIRST, then bumps the pdfBust key so the iframe re-
  // fetches. Lets the operator update the PDF without having to blur/tab
  // out of the field, which was the reported friction point. If nothing
  // is dirty, just bust the preview key.
  const refreshPreview = useCallback(async () => {
    const dirty = activeCell && (
      (activeCell.cell_type === 'template_field' && draft !== activeCell.value) ||
      (activeCell.cell_type === 'condition_script' && (draft !== activeCell.value || JSON.stringify(scriptRoofTypes) !== JSON.stringify(activeCell.roof_types || [])))
    );
    if (dirty) {
      await saveCell();          // saveCell already bumps pdfBust on success
    } else {
      setPdfBust(b => b + 1);
    }
  }, [activeCell, draft, scriptRoofTypes, saveCell]);

  const verifyCell = useCallback(async () => {
    if (!activeCell) return;
    try {
      await api.post('/script-audit/verify', {
        variant, roof_key: roofKey,
        cell_type: activeCell.cell_type, cell_key: activeCell.cell_key,
      });
      patchStatusLocal(activeIdx, 'verified');
    } catch (e) {
      alert('Verify failed: ' + (e?.response?.data?.detail || e.message));
    }
  }, [api, activeCell, activeIdx, variant, roofKey]);

  const nextUnaudited = () => {
    const from = activeIdx + 1;
    const idx = cells.findIndex((c, i) => i >= from && c.status === 'pending');
    if (idx >= 0) setActiveIdx(idx);
    else {
      const first = cells.findIndex(c => c.status === 'pending');
      if (first >= 0) setActiveIdx(first);
    }
  };

  const runAiSuggest = async (tone = '') => {
    if (!activeCell || (activeCell.cell_type !== 'template_field' && activeCell.cell_type !== 'condition_script')) return;
    setAiBusy(true);
    setAiTone(tone);
    try {
      const { data } = await api.post('/script-audit/ai-suggest', {
        variant, roof_key: roofKey, cell_key: activeCell.cell_key,
        current_text: draft, tone,
      });
      setAiAlternates(data.alternates || []);
    } catch (e) {
      alert('AI suggest failed: ' + (e?.response?.data?.detail || e.message));
    } finally {
      setAiBusy(false);
    }
  };

  const resetAudit = async () => {
    if (!window.confirm(`Reset audit progress for ${variant} × ${roofKey}? All cells flip back to 🔴.`)) return;
    try {
      await api.post('/script-audit/reset', { variant, roof_key: roofKey });
      loadCells();
    } catch (e) {
      alert('Reset failed: ' + (e?.response?.data?.detail || e.message));
    }
  };

  // ── Section reorder (iter295) ───────────────────────────────────────
  // The picker's "Report Body" tab lists cells in the PDF section order;
  // dragging a row / pressing ▲▼ / clicking Reset all move the underlying
  // SECTION not just the individual cell — since multiple cells can share
  // one section (e.g. all 3 closing_paragraph_* live under `closing`).
  const moveSection = useCallback(async (sectionKey, delta) => {
    if (!sectionKey) return;
    const idx = sectionOrder.indexOf(sectionKey);
    if (idx < 0) return;
    const newIdx = idx + delta;
    if (newIdx < 0 || newIdx >= sectionOrder.length) return;
    const next = [...sectionOrder];
    next.splice(idx, 1);
    next.splice(newIdx, 0, sectionKey);
    setSectionOrder(next);
    // iter297e — In Draft-Edit mode, keep changes in local state only.
    if (editingDraftId) { setDraftDirty(true); setPdfBust(b => b + 1); return; }
    try {
      await api.put('/script-audit/section-order', { variant, section_keys: next });
      loadCells();
      setPdfBust(b => b + 1);
    } catch (e) {
      alert('Reorder failed: ' + (e?.response?.data?.detail || e.message));
      loadCells();  // revert
    }
  }, [api, variant, sectionOrder, loadCells, editingDraftId]);

  const moveSectionTo = useCallback(async (sourceKey, targetKey) => {
    if (!sourceKey || !targetKey || sourceKey === targetKey) return;
    const src = sectionOrder.indexOf(sourceKey);
    const tgt = sectionOrder.indexOf(targetKey);
    if (src < 0 || tgt < 0) return;
    const next = [...sectionOrder];
    next.splice(src, 1);
    // Insert AT the target's current position — if source was before
    // the target, target's index shifts down by one after the removal.
    const insertAt = src < tgt ? tgt : tgt;
    next.splice(insertAt, 0, sourceKey);
    setSectionOrder(next);
    if (editingDraftId) { setDraftDirty(true); setPdfBust(b => b + 1); return; }
    try {
      await api.put('/script-audit/section-order', { variant, section_keys: next });
      loadCells();
      setPdfBust(b => b + 1);
    } catch (e) {
      alert('Reorder failed: ' + (e?.response?.data?.detail || e.message));
      loadCells();
    }
  }, [api, variant, sectionOrder, loadCells, editingDraftId]);

  const resetSectionOrder = useCallback(async () => {
    if (editingDraftId) {
      // Reset within draft-edit: restore draft's own section_order.
      if (!window.confirm('Reset section order to the draft\'s last-saved state?')) return;
      return discardDraftEdits();
    }
    if (!window.confirm(`Reset section order for the ${variant.replace('_', ' ')} variant to the shipped default? This affects all 6 roof types.`)) return;
    try {
      await api.post('/script-audit/section-order/reset', null, { params: { variant } });
      loadCells();
      setPdfBust(b => b + 1);
    } catch (e) {
      alert('Reset failed: ' + (e?.response?.data?.detail || e.message));
    }
  }, [api, variant, loadCells, editingDraftId, discardDraftEdits]);

  // ── iter296 · Illustration layout + estimate-row width controls ────────
  // Layouts are global (shared across variants) but the active layout
  // choice + width settings are variant-scoped and persisted to all 6
  // roof-key templates of the variant.
  const loadIllustrationLayouts = useCallback(async () => {
    try {
      const { data } = await api.get('/script-audit/illustration-layouts');
      setIllustrationLayouts(data.layouts || []);
    } catch { /* silent — dropdown falls back to shipped presets */ }
  }, [api]);

  useEffect(() => { loadIllustrationLayouts(); }, [loadIllustrationLayouts]);

  const applyIllustrationLayout = useCallback(async (layout_id, extraFields = {}) => {
    if (editingDraftId) {
      // Draft-edit mode: update local state only.
      setIllustrationLayout(layout_id);
      setDraftDirty(true);
      setPdfBust(b => b + 1);
      return;
    }
    try {
      // If it's a custom layout, we must pass anchor_section_keys so the
      // backend can flatten them onto the template docs.
      const custom = illustrationLayouts.find(l => l.id === layout_id && !l.is_shipped);
      const payload = { variant, layout_id, ...extraFields };
      if (custom) {
        payload.anchor_section_keys = custom.anchor_section_keys || [];
        if (custom.left_col_mm != null) payload.left_col_mm = custom.left_col_mm;
        if (custom.right_col_mm != null) payload.right_col_mm = custom.right_col_mm;
      }
      await api.put('/script-audit/template/illustration-layout', payload);
      setIllustrationLayout(layout_id);
      setPdfBust(b => b + 1);
      loadCells();
    } catch (e) {
      alert('Apply layout failed: ' + (e?.response?.data?.detail || e.message));
    }
  }, [api, variant, illustrationLayouts, loadCells, editingDraftId]);

  const applyEstimateRowWidths = useCallback(async (patch) => {
    // Merge with current values so a partial change never accidentally
    // resets the other two dimensions.
    const next = { ...estRowWidths, ...patch };
    setEstRowWidths(next);
    if (editingDraftId) { setDraftDirty(true); setPdfBust(b => b + 1); return; }
    try {
      await api.put('/script-audit/template/estimate-row-widths', { variant, ...next });
      setPdfBust(b => b + 1);
    } catch (e) {
      alert('Width update failed: ' + (e?.response?.data?.detail || e.message));
    }
  }, [api, variant, estRowWidths, editingDraftId]);

  const createCustomLayout = useCallback(async ({ name, anchor_section_keys, left_col_mm, right_col_mm }) => {
    try {
      const { data } = await api.post('/script-audit/illustration-layouts', {
        name, anchor_section_keys, left_col_mm, right_col_mm,
      });
      await loadIllustrationLayouts();
      return data;
    } catch (e) {
      const msg = e?.response?.data?.detail || e.message;
      alert('Create failed: ' + msg);
      return null;
    }
  }, [api, loadIllustrationLayouts]);

  const deleteCustomLayout = useCallback(async (id) => {
    if (!window.confirm('Delete this custom illustration layout? Templates already using it will fall back to standalone.')) return;
    try {
      await api.delete(`/script-audit/illustration-layouts/${id}`);
      loadIllustrationLayouts();
    } catch (e) {
      alert('Delete failed: ' + (e?.response?.data?.detail || e.message));
    }
  }, [api, loadIllustrationLayouts]);

  // ── Per-cell Include-in-PDF flip (iter295 · was already in right pane) ─
  // Surfacing it inline in the picker lets the operator hide/show sections
  // without opening the cell. Only meaningful for template_field cells —
  // estimate_row + condition_script cells don't participate in section_flags.
  const toggleIncludeForCell = useCallback(async (cell) => {
    if (!cell || cell.cell_type !== 'template_field') return;
    if (cell.cell_key === 'roof_details_fields') return;   // always on
    const nextVal = !cell.include_in_pdf;
    // Optimistic local update so the picker flips instantly.
    setCells(prev => prev.map(c => (c.cell_key === cell.cell_key && c.cell_type === cell.cell_type)
      ? { ...c, include_in_pdf: nextVal } : c));
    if (editingDraftId) { setDraftDirty(true); setPdfBust(b => b + 1); return; }
    try {
      await api.put('/script-audit/section-flag', {
        variant, roof_key: roofKey, cell_key: cell.cell_key, include: nextVal,
      });
      setPdfBust(b => b + 1);
    } catch (e) {
      alert('Toggle failed: ' + (e?.response?.data?.detail || e.message));
      loadCells();
    }
  }, [api, variant, roofKey, loadCells, editingDraftId]);

  // ── Insert Roof Details token at the cursor position ────────────────
  // Click on any `{brand}` / `{roof_type}` chip in the palette to splice
  // that placeholder into the draft at the current caret location.
  // Falls back to appending at the end when the textarea hasn't been
  // focused yet (fresh cell load). Pushes an undo snapshot so the user
  // can back out of an accidental insert.
  const insertToken = useCallback((tokenKey) => {
    if (!activeCell || activeCell.cell_key === 'roof_details_fields') return;
    const ta = textareaRef.current;
    const token = `{${tokenKey}}`;
    snapshotForUndo();
    setDraft((prev) => {
      if (!ta || typeof ta.selectionStart !== 'number') {
        return (prev || '') + token;
      }
      const start = ta.selectionStart;
      const end = ta.selectionEnd;
      const next = (prev || '').slice(0, start) + token + (prev || '').slice(end);
      // Restore caret to just AFTER the inserted token on the next tick
      // so the operator can keep typing seamlessly.
      requestAnimationFrame(() => {
        try {
          ta.focus();
          const pos = start + token.length;
          ta.setSelectionRange(pos, pos);
        } catch { /* noop */ }
      });
      return next;
    });
  }, [activeCell]);

  // Auto-save on textarea blur so operator can freely tab between cells.
  // Always bump pdfBust so the left-side iframe re-fetches — even when
  // nothing changed the operator gets a fresh render (useful after they
  // edit a linked condition_script upstream and jump back).
  const onDraftBlur = () => {
    if (!activeCell) return;
    const changed =
      (activeCell.cell_type === 'template_field' && draft !== activeCell.value) ||
      (activeCell.cell_type === 'condition_script' && (draft !== activeCell.value || JSON.stringify(scriptRoofTypes) !== JSON.stringify(activeCell.roof_types || [])));
    if (changed) {
      saveCell();          // saveCell already bumps pdfBust on success
    } else {
      setPdfBust(b => b + 1);  // no-change blur → still refresh preview
    }
  };

  // ── Template-preview strip (Before → THIS → After) ─────────────────
  // Only meaningful for template_field cells. Computed from the ordered
  // TEMPLATE_FIELD_CELLS catalogue so the strip is deterministic even
  // when the picker is filtered/sorted.
  const templateStrip = useMemo(() => {
    if (!activeCell || activeCell.cell_type !== 'template_field') return null;
    const tplCells = cells.filter(c => c.cell_type === 'template_field');
    const pos = tplCells.findIndex(c => c.cell_key === activeCell.cell_key);
    if (pos < 0) return null;
    const trim = (s, n = 60) => {
      // `roof_details_fields` cells carry an array value — render a
      // human-readable summary instead of crashing on .trim().
      if (Array.isArray(s)) return `${s.length} cells · ${s.slice(0, 3).join(' · ')}${s.length > 3 ? '…' : ''}`;
      const t = (s || '').trim().replace(/\s+/g, ' ');
      return t.length > n ? t.slice(0, n) + '…' : (t || '(empty)');
    };
    return {
      prev: pos > 0 ? { ...tplCells[pos - 1], _preview: trim(tplCells[pos - 1].value) } : null,
      curr: { ...activeCell, _preview: trim(draft || activeCell.value) },
      next: pos < tplCells.length - 1 ? { ...tplCells[pos + 1], _preview: trim(tplCells[pos + 1].value) } : null,
    };
  }, [activeCell, cells, draft]);

  const jumpToTemplateCell = (cellKey) => {
    const i = cells.findIndex(c => c.cell_type === 'template_field' && c.cell_key === cellKey);
    if (i >= 0) setActiveIdx(i);
  };

  // ── Copy tools + duplicate propagation ──────────────────────────────
  const loadCopySources = useCallback(async () => {
    if (copySources.length) return;   // cache — 200+ sources are heavy to re-fetch
    try {
      const { data } = await api.get('/script-audit/copy-source-list');
      setCopySources(data.sources || []);
    } catch (e) {
      alert('Failed to load copy sources: ' + (e?.response?.data?.detail || e.message));
    }
  }, [api, copySources.length]);

  const applyCopyFromSource = (src) => {
    if (!src) return;
    // Snapshot for undo before overwriting.
    snapshotForUndo();
    // iter297i · Use full body when available (fallback to preview for
    // legacy sources without the body field).
    setDraft(src.body || src.preview || '');
    setShowCopyTools(false);
  };

  const propagateToDuplicates = async () => {
    if (!activeCell || activeCell.cell_type !== 'condition_script') return;
    const targets = activeCell.duplicate_of || [];
    if (!targets.length) return;
    if (!window.confirm(`Copy this script's text to ${targets.length} near-duplicate row${targets.length === 1 ? '' : 's'}? Their existing text will be overwritten.`)) return;
    setCopyBusy(true);
    try {
      await api.post('/script-audit/copy-to-duplicates', {
        source_cell_key: activeCell.cell_key,
        target_cell_keys: targets,
      });
      alert(`Copied to ${targets.length} rows.`);
      loadCells();
    } catch (e) {
      alert('Copy-to-duplicates failed: ' + (e?.response?.data?.detail || e.message));
    } finally {
      setCopyBusy(false);
    }
  };

  const toggleRoofTick = (rt) => {
    snapshotForUndo();
    setScriptRoofTypes(prev => prev.includes(rt) ? prev.filter(x => x !== rt) : [...prev, rt]);
  };

  // ── Phase D · Include-in-PDF toggle (per template_field cell) ──
  const toggleIncludeInPdf = async (include) => {
    if (!activeCell || activeCell.cell_type !== 'template_field') return;
    // Optimistic local update so the checkbox flips instantly.
    setCells(prev => {
      const n = [...prev];
      if (n[activeIdx]) n[activeIdx] = { ...n[activeIdx], include_in_pdf: include };
      return n;
    });
    try {
      await api.put('/script-audit/section-flag', {
        variant, roof_key: roofKey,
        cell_key: activeCell.cell_key,
        include,
      });
      setPdfBust(b => b + 1);   // refresh preview so change is visible
    } catch (e) {
      alert('Include toggle failed: ' + (e?.response?.data?.detail || e.message));
      loadCells();  // revert on error
    }
  };

  // Dedicated save path for `roof_details_fields` — the grid editor sends
  // an array of 12 keys; we PUT it straight through so the value never has
  // to round-trip via the textarea `draft` state.
  const saveRoofDetailsOrder = async (newArray) => {
    if (!activeCell || activeCell.cell_key !== 'roof_details_fields') return;
    setSaveState('saving');
    try {
      await api.put('/script-audit/cell', {
        variant, roof_key: roofKey,
        cell_type: 'template_field', cell_key: 'roof_details_fields',
        value: newArray,
      });
      patchStatusLocal(activeIdx, 'edited');
      setCells(prev => {
        const n = [...prev];
        if (n[activeIdx]) n[activeIdx] = { ...n[activeIdx], value: newArray };
        return n;
      });
      setSaveState('saved');
      setPdfBust(b => b + 1);
      setTimeout(() => setSaveState('idle'), 900);
    } catch (e) {
      setSaveState('error');
      alert('Grid save failed: ' + (e?.response?.data?.detail || e.message));
    }
  };

  // ── Phase B · Rich formatting handlers ─────────────────────────
  const activeFmtKey = activeCell ? `${activeCell.cell_type}::${activeCell.cell_key}` : null;
  const activeFormatting = activeFmtKey ? (formattingMap[activeFmtKey] || {}) : {};

  const applyFormattingPatch = async (patch) => {
    if (!activeCell) return;
    const next = { ...activeFormatting, ...patch };
    setFormattingMap(prev => ({ ...prev, [activeFmtKey]: next }));
    if (editingDraftId) { setDraftDirty(true); setPdfBust(b => b + 1); return; }
    try {
      await api.put('/script-audit/formatting', {
        variant, roof_key: roofKey,
        cell_type: activeCell.cell_type, cell_key: activeCell.cell_key,
        ...patch,
      });
      setPdfBust(b => b + 1);   // refresh preview
    } catch (e) {
      alert('Failed to save formatting: ' + (e?.response?.data?.detail || e.message));
      loadFormatting();
    }
  };

  const resetFormatting = async () => {
    if (!activeCell) return;
    setFormattingMap(prev => { const n = { ...prev }; delete n[activeFmtKey]; return n; });
    if (editingDraftId) { setDraftDirty(true); setPdfBust(b => b + 1); return; }
    try {
      await api.delete('/script-audit/formatting', {
        params: { variant, roof_key: roofKey, cell_type: activeCell.cell_type, cell_key: activeCell.cell_key },
      });
      setPdfBust(b => b + 1);
    } catch (e) {
      alert('Reset formatting failed: ' + (e?.response?.data?.detail || e.message));
      loadFormatting();
    }
  };

  const toggleSelectCell = (cell) => {
    const k = `${cell.cell_type}::${cell.cell_key}`;
    setSelectedCells(prev => {
      const n = new Set(prev);
      if (n.has(k)) n.delete(k); else n.add(k);
      return n;
    });
  };

  const bulkApplyFormatting = async () => {
    if (!activeCell || selectedCells.size === 0) return;
    if (!window.confirm(`Apply this cell's formatting to ${selectedCells.size} selected cell(s)?`)) return;
    const targets = Array.from(selectedCells).map(k => {
      const [ct, ...rest] = k.split('::');
      return { cell_type: ct, cell_key: rest.join('::') };
    });
    try {
      const { data } = await api.post('/script-audit/formatting/bulk', {
        variant, roof_key: roofKey,
        source_cell_type: activeCell.cell_type, source_cell_key: activeCell.cell_key,
        targets,
      });
      alert(`Formatting applied to ${data.applied} cells.`);
      loadFormatting();
      setPdfBust(b => b + 1);
      setSelectedCells(new Set());
      setMultiSelectMode(false);
    } catch (e) {
      alert('Bulk apply failed: ' + (e?.response?.data?.detail || e.message));
    }
  };

  // ── Linked-report cross-report bulk actions ──────────────────────
  const toggleLinkSelected = (l) => {
    const k = `${l.variant}::${l.roof_key}`;
    setSelectedLinks(prev => {
      const n = new Set(prev);
      if (n.has(k)) n.delete(k); else n.add(k);
      return n;
    });
  };
  const selectAllLinksInCurrentVariant = () => {
    const keys = linkedReports.filter(l => l.variant === variant).map(l => `${l.variant}::${l.roof_key}`);
    setSelectedLinks(new Set(keys));
  };
  const clearLinkSelection = () => setSelectedLinks(new Set());

  const linkTargets = () => Array.from(selectedLinks).map(k => {
    const [v, rk] = k.split('::');
    return { variant: v, roof_key: rk };
  }).filter(t => !(t.variant === variant && t.roof_key === roofKey));   // never target self

  const applyFormattingToLinked = async () => {
    if (!activeCell || selectedLinks.size === 0) return;
    const targets = linkTargets();
    if (targets.length === 0) { alert('Only the current report is selected — pick at least one other linked report.'); return; }
    if (!window.confirm(`Overwrite formatting on ${targets.length} linked report${targets.length === 1 ? '' : 's'}?`)) return;
    try {
      const { data } = await api.post('/script-audit/formatting/bulk', {
        variant, roof_key: roofKey,
        source_cell_type: activeCell.cell_type, source_cell_key: activeCell.cell_key,
        targets: targets.map(t => ({ ...t, cell_type: activeCell.cell_type, cell_key: activeCell.cell_key })),
      });
      alert(`Formatting pushed to ${data.applied} linked report${data.applied === 1 ? '' : 's'}.`);
      setPdfBust(b => b + 1);
    } catch (e) {
      alert('Apply-to-linked failed: ' + (e?.response?.data?.detail || e.message));
    }
  };

  const copyTextToLinked = async () => {
    if (!activeCell || activeCell.cell_type !== 'template_field' || selectedLinks.size === 0) return;
    const targets = linkTargets();
    if (targets.length === 0) { alert('Only the current report is selected — pick at least one other linked report.'); return; }
    if (!window.confirm(`Overwrite the text on ${targets.length} linked report${targets.length === 1 ? '' : 's'} with the current wording?`)) return;
    try {
      const { data } = await api.post('/script-audit/text/bulk-copy', {
        source_variant: variant, source_roof_key: roofKey,
        cell_key: activeCell.cell_key,
        targets,
      });
      alert(`Wording pushed to ${data.applied} linked report${data.applied === 1 ? '' : 's'}.`);
      setPdfBust(b => b + 1);
    } catch (e) {
      alert('Copy-text-to-linked failed: ' + (e?.response?.data?.detail || e.message));
    }
  };

  // ── Matrix view mode (iter288 3C) ────────────────────────────────
  const loadMatrix = useCallback(async () => {
    try {
      const { data } = await api.get('/script-audit/matrix', { params: { variant } });
      setMatrixData(data);
    } catch (e) {
      alert('Failed to load matrix: ' + (e?.response?.data?.detail || e.message));
    }
  }, [api, variant]);
  useEffect(() => { if (viewMode === 'matrix') loadMatrix(); }, [viewMode, loadMatrix]);

  const bulkVerify = async (opts) => {
    // opts = {roof_key} for column or {cell_type, cell_key} for row.
    try {
      await api.post('/script-audit/matrix/bulk-verify', { variant, ...opts });
      loadMatrix(); loadCells();
    } catch (e) {
      alert('Bulk verify failed: ' + (e?.response?.data?.detail || e.message));
    }
  };

  // ── Suggest-similar sources chip (Copy Tools drawer) ────────────
  const suggestSimilar = async () => {
    if (!draft || draft.length < 8) return;
    setSuggestBusy(true);
    try {
      const { data } = await api.get('/script-audit/suggest-similar-sources', {
        params: { text: draft, limit: 8 },
      });
      setSuggestions(data.suggestions || []);
    } catch (e) {
      alert('Suggest similar failed: ' + (e?.response?.data?.detail || e.message));
    } finally {
      setSuggestBusy(false);
    }
  };

  // ── Render helpers ──────────────────────────────────────────────────
  const pdfPreviewUrl = useMemo(() => {
    if (!refAppraisalId) return null;
    const base = process.env.REACT_APP_BACKEND_URL;
    // Append ?token= so the iframe authenticates via the backend's query-
    // param fallback in `get_current_user`. Iframes can't send Bearer
    // headers, and the httponly cookie only lasts 1 hour — the localStorage
    // token stays fresh via the refresh flow, so use it directly.
    const tok = typeof window !== 'undefined' ? localStorage.getItem('access_token') : '';
    // iter298r — Quotation Letter variant lives on a different endpoint.
    // Skip all the draft/variant/roof_key plumbing (Quotation Letter is
    // not template-driven — it renders from live appraisal + quote_type
    // data). Only the auth token + cache-buster ride along.
    if (variant === 'quotation_letter') {
      const q = `_bust=${pdfBust}`;
      return `${base}/api/reports/quotation-detailed/${refAppraisalId}?${q}`;
    }
    // iter299x — Invoice Letter variant. Same auth+cache-buster plumbing
    // as the Quotation Letter above; renders from `/reports/invoice-letter`.
    if (variant === 'invoice_letter') {
      const q = `_bust=${pdfBust}`;
      return `${base}/api/reports/invoice-letter/${refAppraisalId}?${q}`;
    }
    // iter300a — Status Letter variants. Variant key format:
    // `status_{status_key}` → maps to `/reports/status-letter/{status_key}/{id}`.
    if (variant && variant.startsWith('status_')) {
      const statusKey = variant.slice('status_'.length);
      const q = `_bust=${pdfBust}`;
      return `${base}/api/reports/status-letter/${statusKey}/${refAppraisalId}?${q}`;
    }
    // iter297e — when editing a draft, force the PDF preview to render
    // using that draft so operators see their in-progress changes. When
    // editing the raw template, pass draft_id=none so the backend skips
    // the default-draft resolution and renders the template as-is.
    const draftParam = editingDraftId ? `&draft_id=${encodeURIComponent(editingDraftId)}` : `&draft_id=none`;
    // iter298 · Studio-preview roof-key lock — force the backend to load
    // the (variant, roofKey) template the operator is editing, regardless
    // of the reference appraisal's actual roof_type. Otherwise the preview
    // silently rendered the fallback longrun_iron template for any
    // reference appraisal whose roof_type didn't match `match_terms`.
    const forceKey = roofKey ? `&force_roof_key=${encodeURIComponent(roofKey)}` : '';
    const q = `variant=${variant}&inline=1&_bust=${pdfBust}${draftParam}${forceKey}`;
    return `${base}/api/reports/detailed-roof-appraisal/${refAppraisalId}?${q}`;
  }, [refAppraisalId, variant, roofKey, pdfBust, editingDraftId]);

  // iter298d · Build a preview URL for an ARBITRARY draft id (used by the
  // Draft Compare modal — each pane needs its own URL that differs ONLY
  // in the `draft_id` query param). Shares the same auth + force_roof_key
  // + variant + reference-appraisal plumbing as `pdfPreviewUrl`.
  const buildComparePreviewUrl = useCallback((draftId) => {
    if (!refAppraisalId) return '';
    const base = process.env.REACT_APP_BACKEND_URL;
    const tok = localStorage.getItem('token');
    const forceKey = roofKey ? `&force_roof_key=${encodeURIComponent(roofKey)}` : '';
    const q = `variant=${variant}&inline=1&draft_id=${encodeURIComponent(draftId)}${forceKey}`;
    return `${base}/api/reports/detailed-roof-appraisal/${refAppraisalId}?${q}`;
  }, [refAppraisalId, variant, roofKey]);

  // iter298 · Manual PDF refresh — Ctrl/⌘+R re-renders the preview
  // without needing to blur/save the active field. Auto-saves any
  // pending edit first (via `refreshPreview`), then bumps pdfBust.
  // Uses capture-phase so it wins over browser default full-page
  // reload only when the Studio owns focus.
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && (e.key === 'r' || e.key === 'R')) {
        e.preventDefault();
        e.stopPropagation();
        refreshPreview();
      }
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [refreshPreview]);

  return (
    <div className="h-full flex flex-col" data-testid="script-audit-page">
      {/* Header — variant + roof selectors + progress */}
      <div className="bg-white border-b border-[#E2E2DF] px-6 py-4">
        <div className="flex flex-wrap items-center gap-4">
          <div>
            <h1 className="text-xl font-bold text-slate-900 font-[Manrope] tracking-tight flex items-center gap-2">
              <ScrollText size={20} className="text-[#316536]" /> Script Audit Studio
            </h1>
            <p className="text-[11px] text-slate-500 mt-0.5">Guided, cell-by-cell wording + rate audit for the 3 report variants. Auto-save · 🤖 Claude Sonnet 4.6 suggestions · resettable every 3-6 months.</p>
          </div>
          <div className="ml-auto flex items-center gap-2">
            <button data-testid="open-token-registry-btn" onClick={() => { window.location.href = '/token-registry'; }}
              className="flex items-center gap-1.5 px-2.5 py-1 text-[11px] font-semibold text-slate-600 hover:text-[#316536] border border-slate-200 rounded transition-colors" title="Token Registry & Audit">
              <ScrollText size={11} /> Tokens
            </button>
            <label className="flex items-center gap-1.5 text-[11px] text-slate-600 cursor-pointer">
              <input type="checkbox" checked={filterUnaudited} onChange={e => setFilterUnaudited(e.target.checked)} data-testid="filter-unaudited-toggle" />
              Show only 🔴 unaudited
            </label>
            <button data-testid="reset-audit-btn" onClick={resetAudit} className="flex items-center gap-1.5 px-2.5 py-1 text-[11px] font-semibold text-slate-500 hover:text-rose-600 border border-slate-200 rounded transition-colors">
              <RotateCcw size={11} /> Reset progress
            </button>
          </div>
        </div>
        <div className="mt-3 flex flex-wrap gap-2">
          {/* iter298n · Detailed Multi landing tile — one-click preview
              shortcuts for the 3 photo-layout drafts (M1/M2/M3) so the
              operator can compare designs without hunting through the
              Design Drafts dropdown. Only surfaces on detailed_multi. */}
          {variant === 'detailed_multi' && refAppraisalId && (
            <div
              data-testid="detailed-multi-landing-tile"
              className="w-full flex items-center gap-3 flex-wrap px-3 py-2 mb-1 rounded-lg bg-gradient-to-r from-purple-50 via-purple-50/60 to-transparent border border-purple-200"
            >
              <div className="min-w-0">
                <div className="text-[10px] font-bold uppercase tracking-[0.14em] text-purple-800">Detailed Multi Studio</div>
                <div className="text-[11px] text-purple-900/80 truncate">
                  Compare photo layouts side-by-side · reorder sections in the
                  <b> Report Body</b> tab below · edits apply to all 6 roof types.
                </div>
              </div>
              <div className="ml-auto flex items-center gap-1.5 flex-wrap">
                {[
                  { key: 'm1', label: 'M1 · Inline thumbs' },
                  { key: 'm2', label: 'M2 · Bullets + Gallery' },
                  { key: 'm3', label: 'M3 · Photo-Left' },
                ].map(l => (
                  <a
                    key={l.key}
                    data-testid={`landing-preview-${l.key}`}
                    href={`${(process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '')}/api/reports/detailed-roof-appraisal/${refAppraisalId}?variant=detailed_multi&layout_override=${l.key}&inline=true`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-2 py-1 text-[10px] font-semibold text-purple-800 border border-purple-300 rounded-md hover:bg-purple-100 transition-colors"
                    title={`Open ${l.label} preview in a new tab`}
                  >
                    {l.label} ↗
                  </a>
                ))}
              </div>
            </div>
          )}
          {/* iter303g — Two-tier tab bar.
              Row 1: class chooser (Appraisal Reports · Quote Letters · Status/Action Letters).
              Row 2: variants scoped to the active class.
              Row 3: roof-type selector — only rendered for the Appraisal Reports class. */}
          <div className="flex gap-1 bg-slate-800 rounded-md p-1" data-testid="class-selector">
            {CLASSES.map(c => (
              <button
                key={c.key}
                data-testid={`class-tab-${c.key}`}
                onClick={() => {
                  if (isEditingDraft && draftDirty && !window.confirm('You have unsaved draft edits. Switch class and lose them?')) return;
                  if (isEditingDraft) { setEditingDraftId(null); setEditingDraftName(''); setDraftDirty(false); }
                  setActiveClass(c.key);
                  // Snap to the first variant in the new class if the
                  // current one doesn't belong to it.
                  if ((VARIANTS.find(v => v.key === variant)?.class) !== c.key) {
                    const first = VARIANTS.find(v => v.class === c.key);
                    if (first) setVariant(first.key);
                  }
                }}
                className={`px-3 py-1.5 rounded text-[11px] font-bold uppercase tracking-wider transition-colors ${activeClass === c.key ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-300 hover:text-white'}`}
              >
                {c.label}
              </button>
            ))}
          </div>
          {/* Variant selector — filtered to the active class */}
          <div className="flex gap-1 bg-slate-100 rounded-md p-1 flex-wrap" data-testid="variant-selector">
            {VARIANTS.filter(v => v.class === activeClass).map(v => (
              <button key={v.key} data-testid={`variant-tab-${v.key}`}
                onClick={() => {
                  if (isEditingDraft && draftDirty && !window.confirm('You have unsaved draft edits. Switch variant and lose them?')) return;
                  if (isEditingDraft) { setEditingDraftId(null); setEditingDraftName(''); setDraftDirty(false); }
                  setVariant(v.key);
                  // Keep class in sync in case the user landed on a
                  // variant belonging to a different class.
                  if (v.class && v.class !== activeClass) setActiveClass(v.class);
                }}
                className={`px-3 py-1.5 rounded text-xs font-semibold transition-colors ${variant === v.key ? `${v.color} text-white shadow-sm` : 'text-slate-600 hover:text-slate-900'}`}>
                {v.label}
              </button>
            ))}
          </div>
          {/* Roof selector — only shown for Appraisal Reports class. Quote Letters and Status/Action Letters are NOT roof-type-scoped. */}
          {activeClass === 'appraisal_reports' && (
            <div className="flex gap-1 bg-slate-100 rounded-md p-1 flex-wrap" data-testid="roof-selector">
              {ROOF_KEYS.map(rk => (
                <button key={rk} data-testid={`roof-tab-${rk}`}
                  onClick={() => {
                    if (isEditingDraft && draftDirty && !window.confirm('You have unsaved draft edits. Switch roof type and lose them?')) return;
                    setRoofKey(rk);
                  }}
                  className={`px-2.5 py-1.5 rounded text-[11px] font-semibold transition-colors ${roofKey === rk ? 'bg-[#316536] text-white' : 'text-slate-600 hover:bg-slate-200'}`}>
                  {rk.replace(/_/g, ' ')}
                </button>
              ))}
            </div>
          )}
          {/* iter297d · Design Drafts dropdown */}
          <div className="relative" data-testid="design-drafts-wrap">
            <button
              data-testid="design-drafts-btn"
              onClick={() => setShowDraftsMenu(s => !s)}
              disabled={draftBusy}
              className="flex items-center gap-1.5 px-2.5 py-1.5 rounded text-[11px] font-semibold bg-purple-100 text-purple-800 hover:bg-purple-200 transition-colors border border-purple-200"
              title="Save and re-apply layout/design snapshots across roof types (same variant)"
            >
              <Save size={11} />
              Design Drafts ({designDrafts.length})
              <ChevronDown size={11} />
            </button>
            {showDraftsMenu && (
              <div
                data-testid="design-drafts-menu"
                className="absolute z-20 top-full left-0 mt-1 w-96 bg-white border border-purple-200 rounded-lg shadow-xl p-3"
                onMouseLeave={() => { /* keep open until explicit close */ }}
              >
                <div className="flex items-center justify-between mb-2 pb-2 border-b border-purple-100">
                  <div className="text-[11px] font-bold uppercase tracking-wider text-purple-800">
                    Drafts for {variant.replace(/_/g, ' ')}
                  </div>
                  <button
                    data-testid="design-drafts-close"
                    onClick={() => setShowDraftsMenu(false)}
                    className="text-slate-400 hover:text-slate-700 text-xs"
                  >✕</button>
                </div>
                <button
                  data-testid="design-drafts-save-current"
                  onClick={saveCurrentAsDraft}
                  disabled={draftBusy}
                  className="w-full flex items-center gap-2 px-3 py-2 bg-[#316536] hover:bg-[#254D28] text-white text-xs font-semibold rounded transition-colors mb-3 disabled:opacity-50"
                >
                  <Save size={12} /> Save current ({roofKey.replace(/_/g, ' ')}) as draft…
                </button>
                {/* iter298d · Compare Drafts trigger — opens a full-screen
                    modal with two synced PDF preview panes so the operator
                    can eyeball two design layouts side by side. Only useful
                    when there are ≥2 drafts on the current variant. */}
                <button
                  data-testid="design-drafts-compare"
                  onClick={() => { setShowCompareModal(true); setShowDraftsMenu(false); }}
                  disabled={draftBusy || designDrafts.length < 2}
                  className="w-full flex items-center gap-2 px-3 py-2 bg-purple-600 hover:bg-purple-700 text-white text-xs font-semibold rounded transition-colors mb-3 disabled:opacity-40 disabled:cursor-not-allowed"
                  title={designDrafts.length < 2 ? 'Need at least 2 drafts on this variant to compare' : 'Open a side-by-side comparison of any two drafts'}
                >
                  🆚 Compare drafts…
                  {designDrafts.length < 2 && (
                    <span className="text-[9px] font-normal opacity-80">(need ≥2 drafts)</span>
                  )}
                </button>
                {designDrafts.length === 0 ? (
                  <div className="text-[11px] text-slate-500 italic px-2 py-6 text-center">
                    No drafts yet for this variant.<br />Save the current design above to reuse it on other roof types.
                  </div>
                ) : (
                  <div className="space-y-1.5 max-h-72 overflow-y-auto">
                    {designDrafts.map(d => (
                      <div
                        key={d._id}
                        data-testid={`design-draft-row-${d._id}`}
                        className={`p-2 border rounded ${d.is_default ? 'border-amber-300 bg-amber-50/50' : 'border-slate-200 hover:border-purple-300 hover:bg-purple-50/30'}`}
                      >
                        <div className="flex items-center gap-2">
                          <button
                            data-testid={`design-draft-toggle-default-${d._id}`}
                            onClick={() => setDraftDefault(d)}
                            disabled={draftBusy}
                            title={d.is_default ? 'Default draft — used by PDF renderer. Click to unset.' : 'Set as default draft for this variant'}
                            className={`text-lg leading-none ${d.is_default ? 'text-amber-500' : 'text-slate-300 hover:text-amber-400'}`}
                          >{d.is_default ? '★' : '☆'}</button>
                          <div className="flex-1 min-w-0">
                            <div className="text-xs font-semibold text-slate-800 truncate flex items-center gap-1">
                              {d.name}
                              {d.is_default && (
                                <span className="text-[9px] px-1.5 py-0.5 bg-amber-500 text-white font-bold rounded uppercase">Default</span>
                              )}
                            </div>
                            <div className="text-[10px] text-slate-500 font-mono">
                              from {d.source_roof_key.replace(/_/g, ' ')}
                              {d.updated_at && <> · upd {String(d.updated_at).slice(0, 10)}</>}
                              {(d.cell_formatting || []).length > 0 && <> · {(d.cell_formatting || []).length} fmt rows</>}
                            </div>
                          </div>
                          <button
                            data-testid={`design-draft-rename-${d._id}`}
                            onClick={() => renameDraft(d)}
                            className="p-1 text-slate-400 hover:text-[#316536]"
                            title="Rename"
                          ><TypeIcon size={11} /></button>
                          <button
                            data-testid={`design-draft-delete-${d._id}`}
                            onClick={() => deleteDraft(d)}
                            className="p-1 text-slate-400 hover:text-rose-600"
                            title="Delete"
                          >✕</button>
                        </div>
                        <div className="flex items-center gap-1.5 mt-2 pl-6">
                          <button
                            data-testid={`design-draft-edit-${d._id}`}
                            onClick={() => enterDraftEdit(d)}
                            disabled={draftBusy || draftSaving || editingDraftId === d._id}
                            className={`flex-1 px-2 py-1 text-[10px] font-semibold rounded transition-colors disabled:opacity-50 ${editingDraftId === d._id ? 'bg-purple-600 text-white' : 'bg-purple-100 hover:bg-purple-200 text-purple-800 border border-purple-300'}`}
                            title="Load this draft into Studio for editing. Layout edits accumulate in memory; Save to commit them to this draft; Exit to return to Template editing."
                          >{editingDraftId === d._id ? '📝 Editing…' : '📝 Edit Draft'}</button>
                          <button
                            data-testid={`design-draft-save-current-${d._id}`}
                            onClick={() => saveCurrentToDraft(d)}
                            disabled={draftBusy || editingDraftId !== null}
                            className="px-2 py-1 text-[10px] font-semibold bg-[#316536] hover:bg-[#254D28] text-white rounded transition-colors disabled:opacity-50"
                            title="Save the CURRENT Studio layout (section order, flags, illustration, widths, formatting) into this draft — replacing what's stored. Disabled while another draft is being edited."
                          >💾 Sync</button>
                          <button
                            data-testid={`design-draft-apply-${d._id}`}
                            onClick={() => applyDraft(d)}
                            disabled={draftBusy || editingDraftId !== null}
                            className="px-2 py-1 text-[10px] font-semibold bg-[#D35400] hover:bg-[#A04000] text-white rounded transition-colors disabled:opacity-50"
                            title="Copy this draft's design INTO a target roof_type's template (opposite direction: draft → template)"
                          >Apply →</button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                <div className="mt-2 pt-2 border-t border-purple-100 text-[10px] text-slate-500 space-y-0.5">
                  <div><strong>📝 Edit Draft</strong> — Load draft into Studio; layout edits stay in memory until you Save.</div>
                  <div><strong>★ Default</strong> — PDF renders (client-facing) use this draft automatically. Only ONE default per variant.</div>
                  <div><strong>💾 Sync</strong> — Overwrites this draft with the current TEMPLATE layout (only when not editing a draft).</div>
                  <div><strong>Apply →</strong> — Copies this draft&apos;s design INTO a roof_type&apos;s template (reverse direction).</div>
                </div>
              </div>
            )}
          </div>
          {/* Progress meter */}
          <div className="ml-auto flex items-center gap-2 text-[11px] text-slate-600">
            <span data-testid="progress-count">
              <span className="font-bold text-slate-900">{progress.verified + progress.edited}</span>
              <span className="mx-1">/</span>
              <span>{progress.total}</span>
              <span className="ml-1 text-slate-500">cells reviewed</span>
            </span>
            <div className="w-32 h-1.5 bg-slate-200 rounded overflow-hidden">
              <div className="h-full bg-[#316536] transition-all" style={{ width: `${progress.total ? (100 * (progress.verified + progress.edited)) / progress.total : 0}%` }} />
            </div>
          </div>
        </div>
      </div>

      {/* iter297e — Draft-edit banner: shown across the top of Studio
          when the operator has loaded a draft for editing. Layout edits
          accumulate in-memory; only Save commits them to the draft. */}
      {isEditingDraft && (
        <div
          data-testid="draft-edit-banner"
          className={`px-6 py-2.5 flex items-center gap-3 border-b-2 ${draftDirty ? 'bg-amber-100 border-amber-400' : 'bg-purple-100 border-purple-400'}`}
        >
          <span className="text-lg">📝</span>
          <div className="flex-1">
            <div className="text-sm font-bold text-slate-800 flex items-center gap-2">
              Editing Draft: <span className="text-purple-900">{editingDraftName}</span>
              {draftDirty ? (
                <span data-testid="draft-dirty-indicator" className="text-[10px] px-2 py-0.5 bg-amber-500 text-white font-bold rounded uppercase animate-pulse">
                  ● Unsaved changes
                </span>
              ) : (
                <span className="text-[10px] px-2 py-0.5 bg-green-500 text-white font-bold rounded uppercase">
                  ✓ Saved
                </span>
              )}
            </div>
            <div className="text-[11px] text-slate-600 mt-0.5">
              Layout &amp; formatting edits go to this draft only. Script text edits still save to the underlying template. Click <strong>Save changes</strong> to commit; <strong>Discard</strong> to revert; <strong>Exit</strong> to return to template editing.
            </div>
          </div>
          <button
            data-testid="draft-save-btn"
            onClick={saveDraftEdits}
            disabled={draftSaving || !draftDirty}
            className="px-4 py-2 text-xs font-bold bg-[#316536] hover:bg-[#254D28] text-white rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >{draftSaving ? 'Saving…' : '💾 Save changes'}</button>
          <button
            data-testid="draft-discard-btn"
            onClick={discardDraftEdits}
            disabled={draftSaving || !draftDirty}
            className="px-3 py-2 text-xs font-semibold bg-white hover:bg-slate-50 text-slate-700 border border-slate-300 rounded transition-colors disabled:opacity-50"
          >↺ Discard</button>
          <button
            data-testid="draft-exit-btn"
            onClick={exitDraftEdit}
            disabled={draftSaving}
            className="px-3 py-2 text-xs font-semibold bg-white hover:bg-slate-50 text-slate-700 border border-slate-300 rounded transition-colors disabled:opacity-50"
          >✕ Exit</button>
        </div>
      )}

      {/* 3-column body */}
      <div className="flex-1 grid grid-cols-12 gap-3 p-3 overflow-hidden bg-slate-50">
        {/* Left — PDF preview */}
        <PdfPreviewPane
          url={pdfPreviewUrl}
          refAppraisalId={refAppraisalId}
          onRefresh={refreshPreview}
          bustKey={pdfBust}
          focusedButton={(
            <button
              data-testid="focused-preview-btn"
              onClick={() => setShowFocusedPreview(s => !s)}
              className="ml-auto text-[10px] font-semibold px-2 py-0.5 border border-purple-300 text-purple-700 rounded hover:bg-purple-50"
              title="Open the full PDF in a floating panel with quick-nav chips for every section"
            >
              🔍 Focused
            </button>
          )}
        />

        {/* iter298r — Quotation Letter variant swaps the entire
            middle+right column pair for a stacked-section editor. Left
            column (PDF preview) is shared. */}
        {variant === 'quotation_letter' || variant === 'invoice_letter' ? (
          <QuotationLetterEditor
            api={api}
            refAppraisalId={refAppraisalId}
            variant={variant}
            onSaved={() => setPdfBust(b => b + 1)}
          />
        ) : (variant || '').startsWith('status_') ? (
          // iter300a — Status Letter placeholder. Shipping PDF preview
          // + admin default text via app_settings.status_letters. Full
          // inline editor arrives once user has validated the PDF format
          // for all 3 samples (Auth / Cover / Testimonial).
          (() => {
            const statusKey = variant.slice('status_'.length);
            const map = {
              appraisal_booked: { icon: '📋', name: 'Appraisal Authorisation Letter', status: 'Appraisal Booked', hint: 'Client signs to authorise inspection. Includes purpose, client details verify, signature/date lines, service interest ☐ checkboxes, credit + liability disclaimer.' },
              quote_sent:       { icon: '✉️', name: 'Cover Letter',                   status: 'Quote Sent',       hint: 'Accompanies the Quotation Letter. Static company/workmanship blurb + RE: property + valid-for-N-days note. No signature.' },
              paid:             { icon: '⭐', name: 'Testimonial Request Letter',     status: 'Paid',             hint: 'Post-payment. Personal favour request for testimonial + before/after photo permission. Client signs to authorise use of quotes/photos.' },
              // iter303g — Placeholder metadata for the 5 status letters
              // scaffolded but not yet designed. Backend PDF endpoint
              // returns 501 with a friendly hint until a sample PDF is
              // provided by the user.
              accepted:         { icon: '🤝', name: 'Acceptance Confirmation Letter', status: 'Accepted',         hint: 'Sent when the client accepts the quotation. Confirms scope, deposit due, indicative start window and next steps.' },
              in_progress:      { icon: '🛠️', name: 'Work-In-Progress Update Letter', status: 'Work in Progress', hint: 'Mid-job status update. Confirms crew on site, milestone reached, expected finish window, and any change-order variations.' },
              completed:        { icon: '✅', name: 'Job-Completion Letter',           status: 'Completed',        hint: 'On completion. Summary of work performed, workmanship warranty statement, care instructions, and prompts to arrange final invoice + payment.' },
              invoiced:         { icon: '🧾', name: 'Invoice-Issued Letter',           status: 'Invoiced',         hint: 'Cover note that accompanies the Invoice Letter. Terms recap (7 days / bank details), thank-you line, and testimonial teaser.' },
              declined:         { icon: '🙏', name: 'Quote-Declined Letter',           status: 'Declined',         hint: 'Sent when the client declines the quotation. Polite acknowledgement, invitation to reconsider, and future-work referral prompt.' },
            };
            const info = map[statusKey] || { icon: '❓', name: statusKey, status: statusKey, hint: 'This status letter has no design yet. Provide a sample PDF from your Access DB.' };
            const supported = supportedStatusKeys.includes(statusKey);
            // iter300b — For supported statuses, render the full inline
            // editor (StatusLetterEditor) instead of the placeholder card.
            if (supported) {
              return (
                <StatusLetterEditor
                  api={api}
                  statusKey={statusKey}
                  onSaved={() => setPdfBust(b => b + 1)}
                />
              );
            }
            return (
              <div className="col-span-12 lg:col-span-7 bg-white border border-[#E2E2DF] rounded-md p-5 space-y-3" data-testid={`status-letter-placeholder-${statusKey}`}>
                <div className="border-b border-slate-200 pb-2 flex items-center gap-2">
                  <span className="text-2xl">{info.icon}</span>
                  <div>
                    <h3 className="text-sm font-bold text-slate-900">{info.name}</h3>
                    <p className="text-[11px] text-slate-500">
                      Triggered when <b>appraisal_status = &ldquo;{info.status}&rdquo;</b>
                    </p>
                  </div>
                  <span className={`ml-auto text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded ${supported ? 'bg-emerald-100 text-emerald-800 border border-emerald-300' : 'bg-amber-100 text-amber-800 border border-amber-300'}`}>
                    {supported ? '✓ PDF ready' : '⚠ awaiting design'}
                  </span>
                </div>
                <div className="bg-sky-50 border border-sky-200 rounded p-3 text-[11px] text-sky-900">
                  <p><b>What this letter contains:</b> {info.hint}</p>
                </div>
                {supported ? (
                  <div className="bg-slate-50 border border-slate-200 rounded p-3 text-[11px] text-slate-700 space-y-1">
                    <p><b>Editable content lives at:</b> <code className="font-mono text-[10px]">app_settings.status_letters.{statusKey}.*</code></p>
                    <p><b>Optional per-quote-type overrides:</b> <code className="font-mono text-[10px]">quote_types.status_{statusKey}_*</code></p>
                    <p className="text-slate-500 italic mt-1">Left pane shows the live PDF. Inline editor with per-cell auto-save + token palette lands in the next iteration once you confirm the PDF format.</p>
                  </div>
                ) : (
                  <div className="bg-amber-50 border border-amber-200 rounded p-3 text-[11px] text-amber-900">
                    <p><b>Not yet designed.</b> The backend returns HTTP 501 for this status. Once you share a sample PDF from your Access database (like you did for the 3 initial letters), I&apos;ll add the template renderer.</p>
                  </div>
                )}
                <div className="bg-slate-50 border border-slate-200 rounded p-3 text-[11px] text-slate-700">
                  <p className="font-semibold text-slate-900 mb-1">Full status list (9 planned):</p>
                  <div className="grid grid-cols-3 gap-x-3 gap-y-1 text-[10px]">
                    {[
                      ['appraisal_booked', 'Appraisal Booked', true],
                      ['quote_sent', 'Quote Sent', true],
                      ['accepted', 'Accepted', false],
                      ['declined', 'Declined', false],
                      ['work_booked', 'Work Booked', false],
                      ['work_in_progress', 'Work In Progress', false],
                      ['completed', 'Completed', false],
                      ['invoiced', 'Invoiced', false],
                      ['paid', 'Paid', true],
                    ].map(([k, label, ok]) => (
                      <div key={k} className={ok ? 'text-emerald-800' : 'text-slate-400'}>
                        {ok ? '✓' : '○'} {label}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            );
          })()
        ) : (<>

        {/* Middle — cell picker */}
        <div className="col-span-12 lg:col-span-3 bg-white border border-[#E2E2DF] rounded-md overflow-hidden flex flex-col" data-testid="cell-picker-pane">
          <div className="px-3 py-2 border-b border-[#E2E2DF] bg-slate-50 flex items-center gap-2 flex-wrap">
            <div className="flex gap-1 bg-white rounded p-0.5 border border-slate-200" data-testid="view-mode-toggle">
              <button
                data-testid="view-mode-sequential"
                onClick={() => setViewMode('sequential')}
                className={`px-2 py-0.5 text-[10px] font-semibold rounded ${viewMode === 'sequential' ? 'bg-[#316536] text-white' : 'text-slate-600'}`}
              >⇉ Sequential</button>
              <button
                data-testid="view-mode-matrix"
                onClick={() => setViewMode('matrix')}
                className={`px-2 py-0.5 text-[10px] font-semibold rounded ${viewMode === 'matrix' ? 'bg-[#316536] text-white' : 'text-slate-600'}`}
              >⚏ Matrix</button>
            </div>
            {viewMode === 'sequential' && (
              <>
                <button
                  data-testid="multi-select-toggle"
                  onClick={() => { setMultiSelectMode(m => !m); if (multiSelectMode) setSelectedCells(new Set()); }}
                  className={`text-[10px] font-semibold px-2 py-0.5 border rounded transition-colors ${multiSelectMode ? 'bg-[#316536] text-white border-[#316536]' : 'border-slate-300 text-slate-600 hover:bg-slate-100'}`}
                  title="Toggle multi-select mode for bulk formatting"
                >
                  {multiSelectMode ? `☑ ${selectedCells.size}` : '☐ Multi'}
                </button>
                <button data-testid="next-unaudited-btn" onClick={nextUnaudited} className="ml-auto text-[10px] font-semibold px-2 py-0.5 border border-[#316536] text-[#316536] hover:bg-[#316536] hover:text-white rounded transition-colors flex items-center gap-1">
                  Next 🔴 <ChevronRight size={10} />
                </button>
              </>
            )}
            {/* iter296 · Layout panel toggle — illustration + estimate widths */}
            <button
              data-testid="layout-panel-toggle"
              onClick={() => setShowLayoutPanel(s => !s)}
              className={`text-[10px] font-semibold px-2 py-0.5 border rounded transition-colors ${showLayoutPanel ? 'bg-[#316536] text-white border-[#316536]' : 'border-slate-300 text-slate-600 hover:bg-slate-100'}`}
              title="Toggle the PDF layout controls (illustration position + estimate-row widths)"
            >
              🎨 Layout
            </button>
          </div>
          {/* iter296 · Compact Layout panel — collapsible */}
          {showLayoutPanel && (
            <div className="px-3 py-2 border-b border-slate-200 bg-purple-50/40" data-testid="layout-panel">
              <div className="text-[9px] font-bold uppercase tracking-[0.14em] text-slate-500 mb-1">Illustration Position</div>
              <div className="flex flex-wrap items-center gap-1 mb-2">
                {illustrationLayouts.map(l => (
                  <button
                    key={l.id}
                    data-testid={`illust-layout-${l.id}`}
                    onClick={() => applyIllustrationLayout(l.id)}
                    className={`px-2 py-0.5 text-[10px] font-semibold rounded border transition-colors ${illustrationLayout === l.id ? 'bg-[#316536] text-white border-[#316536]' : 'border-slate-300 text-slate-600 hover:bg-slate-100 bg-white'}`}
                    title={l.description || l.name}
                  >
                    {l.name}
                    {!l.is_shipped && (
                      <span
                        role="button"
                        data-testid={`illust-layout-delete-${l.id}`}
                        onClick={(e) => { e.stopPropagation(); deleteCustomLayout(l.id); }}
                        className="ml-1 text-[9px] opacity-60 hover:opacity-100"
                        title="Delete this custom layout"
                      >×</span>
                    )}
                  </button>
                ))}
                {illustrationLayouts.filter(l => !l.is_shipped).length < 5 && (
                  <button
                    data-testid="new-custom-layout-btn"
                    onClick={() => setShowCustomLayoutModal(true)}
                    className="px-2 py-0.5 text-[10px] font-semibold rounded border border-dashed border-slate-400 text-slate-600 hover:bg-slate-100 bg-white"
                  >+ New</button>
                )}
              </div>
              <div className="text-[9px] font-bold uppercase tracking-[0.14em] text-slate-500 mb-1">Estimate Row Widths (mm)</div>
              <div className="flex items-center gap-2 text-[10px]">
                <label className="flex items-center gap-1"><span className="text-slate-600 w-8">Label</span>
                  <input
                    data-testid="est-width-label"
                    type="number" min="5" max="150" step="1"
                    value={estRowWidths.label_mm}
                    onChange={e => setEstRowWidths(w => ({ ...w, label_mm: Number(e.target.value) || 0 }))}
                    onBlur={() => applyEstimateRowWidths({ label_mm: estRowWidths.label_mm })}
                    className="w-14 px-1 py-0.5 border border-slate-300 rounded text-center"
                  /></label>
                <label className="flex items-center gap-1"><span className="text-slate-600 w-6">Gap</span>
                  <input
                    data-testid="est-width-gap"
                    type="number" min="0" max="50" step="1"
                    value={estRowWidths.gap_mm}
                    onChange={e => setEstRowWidths(w => ({ ...w, gap_mm: Number(e.target.value) || 0 }))}
                    onBlur={() => applyEstimateRowWidths({ gap_mm: estRowWidths.gap_mm })}
                    className="w-14 px-1 py-0.5 border border-slate-300 rounded text-center"
                  /></label>
                <label className="flex items-center gap-1"><span className="text-slate-600 w-9">Value</span>
                  <input
                    data-testid="est-width-value"
                    type="number" min="5" max="150" step="1"
                    value={estRowWidths.value_mm}
                    onChange={e => setEstRowWidths(w => ({ ...w, value_mm: Number(e.target.value) || 0 }))}
                    onBlur={() => applyEstimateRowWidths({ value_mm: estRowWidths.value_mm })}
                    className="w-14 px-1 py-0.5 border border-slate-300 rounded text-center"
                  /></label>
                <span className="ml-auto text-[9px] italic text-slate-500">total ~{(estRowWidths.label_mm + estRowWidths.gap_mm + estRowWidths.value_mm)}mm</span>
              </div>
            </div>
          )}
          {/* 3-tab picker split — Report Body / Roof Details / Defects.
              Only visible in Sequential mode; Matrix view spans all cells. */}
          {viewMode === 'sequential' && (
            <div data-testid="picker-tabs" className="flex items-center gap-0 px-2 pt-1 border-b border-slate-200">
              {[
                { key: 'report_body',  label: 'Report Body' },
                { key: 'roof_details', label: 'Roof Details' },
                { key: 'defects',      label: 'Defects' },
              ].map(t => {
                const n = tabCounts[t.key] || 0;
                const isActive = pickerTab === t.key;
                const disabled = n === 0;
                return (
                  <button
                    key={t.key}
                    data-testid={`picker-tab-${t.key}`}
                    onClick={() => !disabled && setPickerTab(t.key)}
                    disabled={disabled}
                    className={`flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-semibold border-b-2 transition-colors ${
                      isActive
                        ? 'border-[#316536] text-[#316536] bg-white'
                        : disabled
                          ? 'border-transparent text-slate-300 cursor-not-allowed'
                          : 'border-transparent text-slate-500 hover:text-slate-800 hover:border-slate-300'
                    }`}
                    title={disabled ? `No ${t.label} cells for this report` : t.label}
                  >
                    {t.label}
                    <span className={`text-[9px] font-mono px-1 rounded ${isActive ? 'bg-[#316536] text-white' : 'bg-slate-200 text-slate-600'}`}>
                      {n}
                    </span>
                  </button>
                );
              })}
            </div>
          )}
          <div className="flex-1 overflow-y-auto p-2">
            {viewMode === 'matrix' ? (
              <MatrixGrid data={matrixData} onBulkVerify={bulkVerify} onCellClick={(cellType, cellKey, rk) => {
                setViewMode('sequential'); setRoofKey(rk);
                setTimeout(() => {
                  const i = cells.findIndex(c => c.cell_type === cellType && c.cell_key === cellKey);
                  if (i >= 0) setActiveIdx(i);
                }, 200);
              }} />
            ) : (
              (() => {
                // Report Body tab: PDF-order rendering with drag+arrows+eye toggle
                // Roof Details / Defects tabs: legacy category grouping
                if (pickerTab !== 'report_body') {
                  const grouped = {};
                  visibleCells.forEach(c => { grouped[c.group] = grouped[c.group] || []; grouped[c.group].push(c); });
                  return Object.entries(grouped).map(([group, arr]) => (
                    <div key={group} className="mb-3">
                      <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-400 mb-1 px-1">{group}</div>
                      {arr.map(c => {
                        const realIdx = cells.indexOf(c);
                        const isActive = realIdx === activeIdx;
                        const isRate = c.cell_type === 'estimate_row';
                        const fmtKey = `${c.cell_type}::${c.cell_key}`;
                        const isSelected = selectedCells.has(fmtKey);
                        const hasFormatting = Boolean(formattingMap[fmtKey]);
                        return (
                          <button
                            key={c.cell_key}
                            data-testid={`cell-item-${c.cell_key}`}
                            onClick={() => setActiveIdx(realIdx)}
                            className={`w-full text-left px-2 py-1.5 mb-0.5 rounded flex items-center gap-2 text-[11px] transition-colors ${isActive ? 'bg-[#316536] text-white' : (isSelected ? 'bg-emerald-50 border border-emerald-300' : 'text-slate-700 hover:bg-slate-100')}`}
                          >
                            {multiSelectMode && c.cell_type !== 'estimate_row' && (
                              <input
                                type="checkbox"
                                data-testid={`cell-select-${c.cell_key}`}
                                checked={isSelected}
                                onClick={(e) => { e.stopPropagation(); toggleSelectCell(c); }}
                                onChange={() => {}}
                                className="w-3 h-3 cursor-pointer flex-shrink-0"
                              />
                            )}
                            <StatusIcon status={c.status} />
                            {isRate ? (c.value_only ? <Calculator size={10} className="opacity-70" /> : <TypeIcon size={10} className="opacity-70" />) : (c.cell_type === 'condition_script' ? <ScrollText size={10} className="opacity-70" /> : <TypeIcon size={10} className="opacity-70" />)}
                            <span className="truncate flex-1">{c.label}</span>
                            {hasFormatting && (
                              <span className="text-[9px] font-bold text-purple-600" title="Has custom formatting">Aa</span>
                            )}
                            {c.include_in_pdf === false && (
                              <span className="text-[9px] font-bold text-rose-600" title="Excluded from PDF">✕</span>
                            )}
                            {(c.duplicate_of || []).length > 0 && (
                              <span className="text-[9px] font-bold text-amber-600" title={`${c.duplicate_of.length} near-duplicate(s)`}>🟠{c.duplicate_of.length}</span>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  ));
                }

                // ── PDF-order render for the Report Body tab ──
                // Group the visible cells by their section_key (which the
                // backend attaches per iter295). Sections without cells
                // still appear so the operator can reorder them.
                const cellsBySection = new Map();
                visibleCells.forEach(c => {
                  const sk = c.section_key || '__unassigned__';
                  if (!cellsBySection.has(sk)) cellsBySection.set(sk, []);
                  cellsBySection.get(sk).push(c);
                });
                // Sections rendered in the picker: everything in
                // reorderableSectionKeys plus any residual section_keys
                // that a cell claims but aren't in the reorderable list
                // (safety net for legacy templates).
                const orderedSections = [];
                reorderableSectionKeys.forEach(sk => orderedSections.push(sk));
                cellsBySection.forEach((_v, sk) => {
                  if (sk && !orderedSections.includes(sk)) orderedSections.push(sk);
                });
                // Section labels — human-readable version of the key.
                const sectionLabel = (sk) => (
                  SECTION_LABELS[sk] || sk.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
                );
                return (
                  <>
                    {sectionOrder.length > 0 && (
                      <div className="flex items-center justify-between mb-2 px-1">
                        <span className="text-[9px] italic text-slate-400 uppercase tracking-wider">PDF section order · drag rows to reorder</span>
                        <button
                          data-testid="reset-section-order-btn"
                          onClick={resetSectionOrder}
                          className="text-[9px] font-semibold text-slate-500 hover:text-[#316536] flex items-center gap-1"
                          title={`Reset section order for the ${variant.replace('_', ' ')} variant to the shipped default`}
                        >
                          <RotateCcw size={9} /> Reset order
                        </button>
                      </div>
                    )}
                    {orderedSections.map((sk) => {
                      const secCells = cellsBySection.get(sk) || [];
                      const posInOrder = sectionOrder.indexOf(sk);
                      const canReorder = posInOrder >= 0;
                      const canMoveUp = posInOrder > 0;
                      const canMoveDown = canReorder && posInOrder < sectionOrder.length - 1;
                      const isDragTarget = dragSectionKey && dragSectionKey !== sk;
                      // iter296b · Highlight sections that are currently
                      // anchored by an active `beside_*` illustration
                      // layout — they still reorder via the arrows, but
                      // the mascot is glued to whichever anchor group they
                      // belong to at PDF render time.
                      const activePresetAnchors = illustrationLayout === 'beside_repair_options'
                        ? ['repair_options']
                        : illustrationLayout === 'beside_estimates'
                        ? ['rescrew_estimate', 'repoint_estimate', 'bedpoint_estimate', 'extra_work_estimate', 'restoration_estimate', 'reroof_estimate']
                        : (illustrationLayout === 'custom'
                          ? (illustrationLayouts.find(l => l.id === illustrationLayout)?.anchor_section_keys || [])
                          : []);
                      const isAnchored = activePresetAnchors.includes(sk);
                      return (
                        <div
                          key={sk}
                          data-testid={`section-block-${sk}`}
                          className={`mb-2 rounded transition-shadow ${isDragTarget ? 'ring-2 ring-emerald-400 ring-offset-1 shadow-md' : ''} ${isAnchored ? 'bg-purple-50/60' : ''}`}
                          onDragOver={(e) => { if (canReorder) e.preventDefault(); }}
                          onDrop={(e) => {
                            e.preventDefault();
                            if (dragSectionKey) {
                              moveSectionTo(dragSectionKey, sk);
                              setDragSectionKey(null);
                            }
                          }}
                        >
                          {/* Section header row — drag/reorder controls */}
                          <div className={`flex items-center gap-1 px-1.5 py-1 mb-0.5 rounded-sm border-l-2 ${isAnchored ? 'border-purple-400' : 'border-transparent'} ${canReorder ? 'bg-slate-50/60 hover:bg-slate-100' : ''}`}>
                            {canReorder ? (
                              <span
                                data-testid={`section-drag-${sk}`}
                                draggable
                                onDragStart={() => setDragSectionKey(sk)}
                                onDragEnd={() => setDragSectionKey(null)}
                                title="Drag to reorder this section"
                                className="text-slate-500 hover:text-[#316536] cursor-grab active:cursor-grabbing"
                              >
                                <GripVertical size={13} />
                              </span>
                            ) : (
                              <span className="w-[13px]" />
                            )}
                            <span className="text-[10px] font-bold uppercase tracking-[0.12em] text-slate-600 flex-1 truncate">
                              {sectionLabel(sk)}
                              {isAnchored && (
                                <span className="ml-1.5 text-[9px] font-normal italic text-purple-600" title={`Anchored by "${illustrationLayout.replace('_', ' ')}" illustration layout — reorder still works but the mascot follows this section at PDF render.`}>🔗 anchored</span>
                              )}
                              {secCells.length === 0 && !isAnchored && (
                                <span className="text-[9px] font-normal italic text-slate-400 ml-1">(auto)</span>
                              )}
                            </span>
                            {canReorder && (
                              <>
                                <button
                                  data-testid={`section-up-${sk}`}
                                  disabled={!canMoveUp}
                                  onClick={() => moveSection(sk, -1)}
                                  className={`p-1 rounded ${canMoveUp ? 'text-slate-600 hover:text-white hover:bg-[#316536]' : 'text-slate-300 cursor-not-allowed'}`}
                                  title="Move section up"
                                >
                                  <ChevronUp size={13} />
                                </button>
                                <button
                                  data-testid={`section-down-${sk}`}
                                  disabled={!canMoveDown}
                                  onClick={() => moveSection(sk, 1)}
                                  className={`p-1 rounded ${canMoveDown ? 'text-slate-600 hover:text-white hover:bg-[#316536]' : 'text-slate-300 cursor-not-allowed'}`}
                                  title="Move section down"
                                >
                                  <ChevronDown size={13} />
                                </button>
                              </>
                            )}
                          </div>
                          {/* Cells under this section */}
                          {secCells.map(c => {
                            const realIdx = cells.indexOf(c);
                            const isActive = realIdx === activeIdx;
                            const isRate = c.cell_type === 'estimate_row';
                            const fmtKey = `${c.cell_type}::${c.cell_key}`;
                            const isSelected = selectedCells.has(fmtKey);
                            const hasFormatting = Boolean(formattingMap[fmtKey]);
                            const isExcluded = c.include_in_pdf === false;
                            const canToggleInclude = c.cell_type === 'template_field' && c.cell_key !== 'roof_details_fields';
                            return (
                              <div
                                key={c.cell_key}
                                data-testid={`cell-row-${c.cell_key}`}
                                className={`flex items-center gap-1 px-1 mb-0.5 rounded transition-colors ${isActive ? 'bg-[#316536] text-white' : (isSelected ? 'bg-emerald-50 border border-emerald-300' : 'hover:bg-slate-50')}`}
                              >
                                <button
                                  data-testid={`cell-item-${c.cell_key}`}
                                  onClick={() => setActiveIdx(realIdx)}
                                  className={`flex-1 min-w-0 text-left px-1 py-1 flex items-center gap-2 text-[11px] ${isExcluded && !isActive ? 'opacity-50 line-through' : ''} ${isActive ? 'text-white' : 'text-slate-700'}`}
                                >
                                  {multiSelectMode && c.cell_type !== 'estimate_row' && (
                                    <input
                                      type="checkbox"
                                      data-testid={`cell-select-${c.cell_key}`}
                                      checked={isSelected}
                                      onClick={(e) => { e.stopPropagation(); toggleSelectCell(c); }}
                                      onChange={() => {}}
                                      className="w-3 h-3 cursor-pointer flex-shrink-0"
                                    />
                                  )}
                                  <StatusIcon status={c.status} />
                                  {isRate ? (c.value_only ? <Calculator size={10} className="opacity-70" /> : <TypeIcon size={10} className="opacity-70" />) : (c.cell_type === 'condition_script' ? <ScrollText size={10} className="opacity-70" /> : <TypeIcon size={10} className="opacity-70" />)}
                                  <span className="truncate flex-1">{c.label}</span>
                                  {hasFormatting && (
                                    <span className="text-[9px] font-bold text-purple-600" title="Has custom formatting">Aa</span>
                                  )}
                                  {(c.duplicate_of || []).length > 0 && (
                                    <span className="text-[9px] font-bold text-amber-600" title={`${c.duplicate_of.length} near-duplicate(s)`}>🟠{c.duplicate_of.length}</span>
                                  )}
                                </button>
                                {canToggleInclude && (
                                  <button
                                    data-testid={`cell-include-toggle-${c.cell_key}`}
                                    onClick={(e) => { e.stopPropagation(); toggleIncludeForCell(c); }}
                                    className={`p-1 rounded ${isExcluded ? 'text-rose-500 hover:text-rose-700' : (isActive ? 'text-white/70 hover:text-white' : 'text-slate-400 hover:text-[#316536]')} hover:bg-slate-100`}
                                    title={isExcluded ? 'Excluded from PDF — click to include' : 'Included in PDF — click to exclude'}
                                  >
                                    {isExcluded ? <EyeOff size={11} /> : <Eye size={11} />}
                                  </button>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      );
                    })}
                  </>
                );
              })()
            )}
          </div>
        </div>

        {/* Right — edit form + AI panel */}
        <div className="col-span-12 lg:col-span-4 bg-white border border-[#E2E2DF] rounded-md overflow-y-auto p-4" data-testid="edit-pane">
          {!activeCell ? (
            <div className="text-xs text-slate-400 text-center py-12">Pick a cell on the left to start.</div>
          ) : (
            <>
              <div className="flex items-start justify-between mb-2">
                <div>
                  <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-400">{activeCell.group}</div>
                  <h2 className="text-sm font-bold text-slate-900 font-[Manrope]">{activeCell.label}</h2>
                  <div className="text-[10px] font-mono text-slate-400 mt-0.5">{activeCell.cell_type} · {activeCell.cell_key}</div>
                </div>
                <div className="flex items-center gap-1 text-[10px] font-semibold" data-testid="active-cell-status">
                  <StatusIcon status={activeCell.status} size={14} />
                  <span className={STATUS_META[activeCell.status]?.color || 'text-slate-500'}>{STATUS_META[activeCell.status]?.label}</span>
                </div>
              </div>

              {/* Linked reports chip strip */}
              {linkedReports.length > 0 && (
                <div className="mb-3 p-2 bg-sky-50/70 border border-sky-200 rounded-md" data-testid="linked-reports-box">
                  <div className="flex items-center gap-1.5 mb-1">
                    <Link2 size={11} className="text-sky-700" />
                    <span className="text-[10px] font-bold uppercase tracking-wider text-sky-800">
                      Linked reports ({linkedReports.length})
                    </span>
                    <span className="text-[9px] text-sky-700 italic ml-1">
                      · click chips to target · {selectedLinks.size} selected
                    </span>
                    {/* Shortcuts */}
                    <button
                      data-testid="linked-select-current-variant"
                      onClick={selectAllLinksInCurrentVariant}
                      className="ml-auto text-[9px] font-semibold px-1.5 py-0.5 border border-sky-300 text-sky-700 rounded hover:bg-sky-100"
                      title={`Select all reports on ${variant.replace('_', ' ')}`}
                    >
                      Select all {variant.replace('_', ' ')}
                    </button>
                    {selectedLinks.size > 0 && (
                      <button
                        data-testid="linked-clear-selection"
                        onClick={clearLinkSelection}
                        className="text-[9px] font-semibold px-1.5 py-0.5 border border-slate-300 text-slate-600 rounded hover:bg-slate-100"
                        title="Clear selection"
                      >✕ Clear</button>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {linkedReports.map((l, i) => {
                      const k = `${l.variant}::${l.roof_key}`;
                      const isSelected = selectedLinks.has(k);
                      const isSelf = l.variant === variant && l.roof_key === roofKey;
                      return (
                        <button
                          key={i}
                          data-testid={`linked-chip-${l.variant}-${l.roof_key}`}
                          onClick={() => !isSelf && toggleLinkSelected(l)}
                          disabled={isSelf}
                          title={isSelf ? 'This is the active report' : (isSelected ? 'Click to unselect' : 'Click to select as target')}
                          className={`text-[9px] font-mono px-1.5 py-0.5 rounded border transition-colors ${
                            isSelf
                              ? 'bg-sky-800 text-white border-sky-800 cursor-default'
                              : isSelected
                                ? 'bg-[#316536] text-white border-[#316536] hover:opacity-90'
                                : 'bg-white text-sky-800 border-sky-200 hover:bg-sky-100'
                          }`}
                        >
                          {isSelected ? '✓ ' : ''}{l.variant.replace('_', ' ')} · {l.roof_key.replace('_', ' ')}
                          {isSelf && ' (this)'}
                        </button>
                      );
                    })}
                  </div>
                  {/* Cross-report action buttons */}
                  {selectedLinks.size > 0 && (
                    <div className="mt-2 pt-2 border-t border-sky-200 flex flex-wrap items-center gap-2">
                      <button
                        data-testid="linked-apply-formatting-btn"
                        onClick={applyFormattingToLinked}
                        className="flex-1 min-w-[180px] px-2 py-1 text-[10px] font-semibold bg-purple-600 text-white rounded hover:bg-purple-700"
                        title="Copy this cell's formatting (font, size, colour, B/I/U) to the selected linked reports"
                      >
                        🎨 Apply formatting to {selectedLinks.size} selected
                      </button>
                      {activeCell?.cell_type === 'template_field' && (
                        <button
                          data-testid="linked-copy-text-btn"
                          onClick={copyTextToLinked}
                          className="flex-1 min-w-[180px] px-2 py-1 text-[10px] font-semibold bg-[#316536] text-white rounded hover:bg-[#264c2a]"
                          title="Copy the wording of this cell to the selected linked reports"
                        >
                          📋 Copy text to {selectedLinks.size} selected
                        </button>
                      )}
                    </div>
                  )}
                </div>
              )}

              {activeCell.cell_type === 'template_field' || activeCell.cell_type === 'condition_script' ? (
                <>
                  {/* iter296f — value_only hint for template_field cells
                      whose TEXT is not audit-editable (e.g. Extra Works
                      comment — text comes from the appraisal Roof Details
                      record, only formatting applies here).
                      iter298e — also covers the 8 per-area condition
                      font-only cells (ridge_condition_fmt, …). */}
                  {activeCell.cell_type === 'template_field' && activeCell.value_only && (
                    <div className="mb-3 p-3 bg-purple-50/60 border border-purple-200 rounded-md" data-testid="template-value-only-hint">
                      <div className="text-[11px] text-purple-800">
                        <b>Formatting-only cell</b> — the paragraph text is populated from the{' '}
                        <b>{activeCell.cell_key.endsWith('_condition_fmt') ? 'Appraisal "One Comment Per Area" page' : 'Appraisal Roof Details record'}</b>,{' '}
                        not from the Studio. The <b>Aa toolbar below</b> controls font · size · colour · B / I / U
                        for whatever text comes through.
                      </div>
                    </div>
                  )}
                  {/* Undo/Redo + Copy Tools toolbar */}
                  <div className="flex flex-wrap items-center gap-1 mb-2">
                    <button
                      data-testid="undo-btn"
                      onClick={applyUndo}
                      disabled={undoStack.length === 0}
                      title="Undo last change"
                      className="flex items-center gap-1 px-2 py-1 text-[10px] font-semibold border border-slate-200 text-slate-600 rounded hover:bg-slate-100 disabled:opacity-40"
                    >
                      ↶ Undo{undoStack.length > 0 && <span className="opacity-60">({undoStack.length})</span>}
                    </button>
                    <button
                      data-testid="redo-btn"
                      onClick={applyRedo}
                      disabled={redoStack.length === 0}
                      title="Redo"
                      className="flex items-center gap-1 px-2 py-1 text-[10px] font-semibold border border-slate-200 text-slate-600 rounded hover:bg-slate-100 disabled:opacity-40"
                    >
                      ↷ Redo{redoStack.length > 0 && <span className="opacity-60">({redoStack.length})</span>}
                    </button>
                    <button
                      data-testid="copy-tools-btn"
                      onClick={() => { setShowCopyTools(s => !s); loadCopySources(); }}
                      className="flex items-center gap-1 px-2 py-1 text-[10px] font-semibold border border-sky-300 text-sky-700 rounded hover:bg-sky-50"
                    >
                      📋 Copy from…
                    </button>
                    {activeCell.cell_type === 'condition_script' && (activeCell.duplicate_of || []).length > 0 && (
                      <button
                        data-testid="propagate-duplicates-btn"
                        onClick={propagateToDuplicates}
                        disabled={copyBusy}
                        className="flex items-center gap-1 px-2 py-1 text-[10px] font-semibold border border-amber-400 text-amber-700 rounded hover:bg-amber-50 disabled:opacity-50"
                      >
                        🟠 Apply to {(activeCell.duplicate_of || []).length} duplicate{(activeCell.duplicate_of || []).length === 1 ? '' : 's'}
                      </button>
                    )}
                  </div>

                  {/* Copy Tools drawer — iter297i simplified: default lists
                      only same-cell-type sources across other roof_keys of the
                      current variant. Advanced toggle unlocks full catalogue. */}
                  {showCopyTools && (
                    <div data-testid="copy-tools-drawer" className="mb-3 p-2 bg-sky-50/70 border border-sky-200 rounded-md">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-[10px] font-bold uppercase tracking-wider text-sky-800">
                          {copyAdvanced ? 'Advanced — all sources' : `Copy same cell from another roof type`}
                        </span>
                        <label className="ml-auto flex items-center gap-1 text-[10px] text-slate-600 cursor-pointer" title="Toggle to show ALL cells from every variant/roof (old behaviour)">
                          <input
                            data-testid="copy-advanced-toggle"
                            type="checkbox"
                            checked={copyAdvanced}
                            onChange={e => setCopyAdvanced(e.target.checked)}
                            className="accent-sky-600"
                          />
                          Advanced
                        </label>
                        {copyAdvanced && (
                          <input
                            data-testid="copy-tools-search"
                            value={copySearch}
                            onChange={e => setCopySearch(e.target.value)}
                            placeholder="Search…"
                            className="px-2 py-0.5 text-[11px] border border-sky-300 rounded bg-white min-w-0 w-32"
                          />
                        )}
                        <button onClick={() => { setShowCopyTools(false); setSuggestions([]); setCopyAdvanced(false); }} className="text-slate-400 hover:text-rose-600 text-xs">✕</button>
                      </div>
                      {copyAdvanced && suggestions.length > 0 && (
                        <div data-testid="suggested-sources" className="mb-2 p-1.5 bg-emerald-50 border border-emerald-200 rounded">
                          <div className="text-[9px] font-bold uppercase tracking-wider text-emerald-800 mb-1">Suggested — similar to your draft:</div>
                          <div className="space-y-1 max-h-32 overflow-y-auto">
                            {suggestions.map((s, i) => (
                              <button key={i} data-testid={`suggested-source-${i}`} onClick={() => applyCopyFromSource(s)}
                                className="w-full text-left px-2 py-1 text-[10px] bg-white border border-emerald-200 rounded hover:bg-emerald-100">
                                <div className="flex items-center gap-1">
                                  <span className="font-semibold text-emerald-800 truncate flex-1">{s.label}</span>
                                  <span className="text-[9px] font-mono text-emerald-500">{(s.similarity * 100).toFixed(0)}%</span>
                                </div>
                                <div className="text-slate-600 truncate italic">{s.preview}</div>
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                      <div className="max-h-52 overflow-y-auto space-y-1">
                        {(() => {
                          const all = copySources || [];
                          const filtered = copyAdvanced
                            ? all.filter(s => !copySearch || (s.label + ' ' + (s.preview || '')).toLowerCase().includes(copySearch.toLowerCase())).slice(0, 30)
                            : all.filter(s => s.cell_type === activeCell.cell_type
                                            && s.cell_key === activeCell.cell_key
                                            && s.variant === variant
                                            && s.roof_key !== roofKey);
                          if (filtered.length === 0) {
                            return <div className="text-[10px] text-slate-400 italic px-2 py-4 text-center">
                              {copyAdvanced ? 'No matching sources.' : `No other roof types have text in \u201c${activeCell.cell_key.replace(/_/g,' ')}\u201d yet.`}
                            </div>;
                          }
                          return filtered.map((s, i) => (
                            <button
                              key={i}
                              data-testid={`copy-source-${i}`}
                              onClick={() => applyCopyFromSource(s)}
                              className="w-full text-left px-2 py-1.5 text-[10px] bg-white border border-sky-200 rounded hover:bg-sky-100 hover:border-sky-400 transition-colors"
                            >
                              <div className="font-semibold text-sky-800 truncate">
                                {copyAdvanced
                                  ? s.label
                                  : `${s.roof_key.replace(/_/g,' ')}`}
                              </div>
                              <div className="text-slate-600 truncate italic">{s.preview || '(empty)'}</div>
                            </button>
                          ));
                        })()}
                        {(copySources || []).length === 0 && <div className="text-[10px] text-slate-400 italic">Loading…</div>}
                      </div>
                      {!copyAdvanced && (
                        <div className="mt-2 pt-1.5 border-t border-sky-200 text-[9px] text-slate-500 italic">
                          Showing &ldquo;{activeCell.cell_key.replace(/_/g,' ')}&rdquo; text from other roof types in this variant. Toggle Advanced to browse the full catalogue.
                        </div>
                      )}
                    </div>
                  )}

                  {/* Mini-Matrix (condition_script cells only) */}
                  {activeCell.cell_type === 'condition_script' && (
                    <div data-testid="mini-matrix" className="mb-3 p-2 bg-emerald-50/70 border border-emerald-200 rounded-md">
                      <div className="flex items-center gap-1.5 mb-1.5">
                        <span className="text-[10px] font-bold uppercase tracking-wider text-emerald-800">Roof-type applicability</span>
                        <span className="text-[9px] text-emerald-600 italic ml-auto">{scriptRoofTypes.length === 0 ? '(generic — applies to all roofs)' : `${scriptRoofTypes.length} ticked`}</span>
                      </div>
                      <div className="flex flex-wrap gap-1">
                        {ROOF_KEYS.map(rk => {
                          const label = rk.replace(/_/g, ' ');
                          const ticked = scriptRoofTypes.some(x => (x || '').toLowerCase() === label.toLowerCase() || (x || '').toLowerCase().replace(/\s+/g, '_') === rk);
                          return (
                            <button
                              key={rk}
                              data-testid={`mini-matrix-tick-${rk}`}
                              onClick={() => toggleRoofTick(label)}
                              onBlur={onDraftBlur}
                              className={`px-2 py-0.5 text-[10px] font-semibold rounded border transition-colors ${ticked ? 'bg-emerald-600 text-white border-emerald-600' : 'bg-white text-emerald-700 border-emerald-300 hover:bg-emerald-100'}`}
                            >
                              {ticked ? '✓ ' : ''}{label}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Include-in-PDF toggle — Phase D. Suppresses this
                      section from every rendered PDF for the current
                      (variant, roof_key). Excluded cells still show in the
                      audit picker so the operator can flip them back on. */}
                  {activeCell.cell_type === 'template_field' && (
                    <label className="mb-2 flex items-center gap-2 px-2 py-1.5 bg-emerald-50/70 border border-emerald-200 rounded-md text-[11px] font-semibold text-emerald-900 cursor-pointer" data-testid="include-in-pdf-wrapper">
                      <input
                        data-testid="include-in-pdf-toggle"
                        type="checkbox"
                        checked={activeCell.include_in_pdf !== false}
                        onChange={e => toggleIncludeInPdf(e.target.checked)}
                        className="w-3.5 h-3.5 cursor-pointer"
                      />
                      <span className="flex-1">Include this section in the PDF</span>
                      {activeCell.include_in_pdf === false && (
                        <span className="text-[9px] font-bold uppercase text-rose-600 tracking-wider">✕ excluded</span>
                      )}
                    </label>
                  )}

                  {/* Rich formatting toolbar — Phase B */}
                  <FormattingToolbar
                    value={activeFormatting}
                    fonts={fonts}
                    onChange={applyFormattingPatch}
                    onReset={resetFormatting}
                    selectedCount={selectedCells.size}
                    onBulkApply={bulkApplyFormatting}
                    bulletMode={activeCell.cell_type === 'template_field' && activeCell.cell_key === 'main_repair_options'}
                  />

                  <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Current text</label>
                  {/* Template-preview strip (Before → THIS → After) — deterministic breadcrumb of
                      surrounding template fields so the operator sees context before editing. */}
                  {templateStrip && (
                    <div
                      data-testid="template-preview-strip"
                      className="mt-1 mb-2 grid grid-cols-3 gap-1 text-[9px] font-mono"
                    >
                      {templateStrip.prev ? (
                        <button
                          data-testid="template-strip-prev"
                          onClick={() => jumpToTemplateCell(templateStrip.prev.cell_key)}
                          title={`Jump to previous field · ${templateStrip.prev.label}`}
                          className="text-left px-1.5 py-1 border border-slate-200 rounded bg-slate-50 hover:bg-slate-100 truncate"
                        >
                          <div className="text-[9px] font-bold uppercase tracking-wider text-slate-400">← Before</div>
                          <div className="text-slate-700 font-sans font-semibold truncate">{templateStrip.prev.label}</div>
                          <div className="text-slate-500 italic truncate">{templateStrip.prev._preview}</div>
                        </button>
                      ) : (
                        <div className="text-[9px] italic text-slate-300 px-1.5 py-1 border border-dashed border-slate-200 rounded">— top of report —</div>
                      )}
                      <div className="text-left px-1.5 py-1 border-2 border-[#316536] rounded bg-emerald-50 truncate" data-testid="template-strip-current">
                        <div className="text-[9px] font-bold uppercase tracking-wider text-[#316536]">▼ THIS CELL</div>
                        <div className="text-slate-900 font-sans font-semibold truncate">{templateStrip.curr.label}</div>
                        <div className="text-slate-600 italic truncate">{templateStrip.curr._preview}</div>
                      </div>
                      {templateStrip.next ? (
                        <button
                          data-testid="template-strip-next"
                          onClick={() => jumpToTemplateCell(templateStrip.next.cell_key)}
                          title={`Jump to next field · ${templateStrip.next.label}`}
                          className="text-left px-1.5 py-1 border border-slate-200 rounded bg-slate-50 hover:bg-slate-100 truncate"
                        >
                          <div className="text-[9px] font-bold uppercase tracking-wider text-slate-400">After →</div>
                          <div className="text-slate-700 font-sans font-semibold truncate">{templateStrip.next.label}</div>
                          <div className="text-slate-500 italic truncate">{templateStrip.next._preview}</div>
                        </button>
                      ) : (
                        <div className="text-[9px] italic text-slate-300 px-1.5 py-1 border border-dashed border-slate-200 rounded">— end of report —</div>
                      )}
                    </div>
                  )}
                  {activeCell.cell_key === 'roof_details_fields' ? (
                    // Reused grid editor — same drag-and-drop widget as
                    // Report Setup → Instant Report scripts. Saves on
                    // every rearrangement via the dedicated handler.
                    <div data-testid="roof-details-grid-wrapper" className="mt-1">
                      <RoofDetailsGridEditor
                        fields={Array.isArray(activeCell.value) ? activeCell.value : []}
                        fieldMeta={roofDetailFieldMeta}
                        onChange={saveRoofDetailsOrder}
                      />
                      <p className="text-[10px] text-slate-500 italic mt-2">
                        Drag any cell to swap positions. Row 1 = cells 1-3 (top), Row 4 = cells 10-12 (bottom). Formatting toolbar above applies font / size / colour / B / I / U to the whole Roof Details table.
                      </p>
                    </div>
                  ) : (
                    <>
                      {/* Insert Field palette — grouped chips (Roof
                          Details / Client-Property / Estimates). Click a
                          chip to splice `{key}` at the caret. The PDF
                          renderer swaps these for actual values from the
                          appraisal at render-time; missing roof-details
                          values print `[key]` as a visible marker. */}
                      <div className="mt-1 mb-1.5 space-y-1" data-testid="insert-field-palette">
                        {INSERT_FIELD_GROUPS.map((grp) => (
                          <div key={grp.label} className="flex flex-wrap items-center gap-1">
                            <span className="text-[10px] font-semibold text-slate-500 mr-1 uppercase tracking-wider min-w-[92px]">{grp.label}:</span>
                            {grp.tokens.map((t) => (
                              <button
                                key={t.key}
                                type="button"
                                data-testid={`insert-token-${t.key}`}
                                onClick={() => insertToken(t.key)}
                                title={`Insert {${t.key}} at cursor · resolves to the appraisal's "${t.label}" at PDF render`}
                                className="px-1.5 py-0.5 text-[10px] font-mono border border-slate-200 rounded bg-slate-50 hover:bg-[#316536] hover:text-white hover:border-[#316536] text-slate-700 transition-colors"
                              >
                                {`{${t.key}}`}
                              </button>
                            ))}
                          </div>
                        ))}
                      </div>
                      <textarea
                        ref={textareaRef}
                        data-testid="cell-draft-textarea"
                        value={draft}
                        onChange={e => { snapshotForUndo(); setDraft(e.target.value); }}
                        onBlur={onDraftBlur}
                        rows={8}
                        className="w-full mt-1 px-3 py-2 text-xs border border-[#E2E2DF] rounded-md focus:border-[#316536] focus:outline-none font-mono leading-relaxed"
                        placeholder="(empty — this cell has never been populated)"
                      />
                    </>
                  )}
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {activeCell.cell_key !== 'roof_details_fields' && (
                      <>
                        <button data-testid="save-cell-btn" onClick={saveCell} className="flex items-center gap-1.5 px-2.5 py-1.5 text-[11px] font-semibold bg-[#D35400] text-white hover:bg-[#A04000] rounded-md transition-colors">
                          <Save size={11} /> {saveState === 'saving' ? 'Saving…' : saveState === 'saved' ? 'Saved ✓' : 'Save'}
                        </button>
                        <button data-testid="save-and-next-btn" onClick={async () => { await saveCell(); nextUnaudited(); }} className="flex items-center gap-1.5 px-2.5 py-1.5 text-[11px] font-semibold text-slate-500 hover:text-slate-900 border border-slate-200 rounded-md transition-colors">
                          Save & Next <ChevronRight size={11} />
                        </button>
                      </>
                    )}
                    <button data-testid="verify-cell-btn" onClick={verifyCell} className="flex items-center gap-1.5 px-2.5 py-1.5 text-[11px] font-semibold text-emerald-700 border border-emerald-300 hover:bg-emerald-50 rounded-md transition-colors">
                      <CheckCircle2 size={11} /> Verify
                    </button>
                    {activeCell.cell_key === 'roof_details_fields' && (
                      <span className="text-[10px] text-slate-500 italic self-center ml-1">
                        Auto-saves on every drag & drop — no Save button needed.
                      </span>
                    )}
                  </div>

                  {/* AI suggest panel */}
                  <div className="mt-4 p-3 border border-purple-200 rounded-md bg-purple-50/50">
                    <div className="flex items-center gap-1.5 mb-2">
                      <Wand2 size={12} className="text-purple-700" />
                      <span className="text-[11px] font-bold uppercase tracking-wider text-purple-800">Claude Sonnet 4.6 · Suggest improvements</span>
                    </div>
                    <div className="flex flex-wrap gap-1 mb-2" data-testid="tone-chips">
                      {TONE_CHIPS.map(t => (
                        <button
                          key={t.key || 'default'}
                          data-testid={`tone-chip-${t.key || 'default'}`}
                          onClick={() => runAiSuggest(t.key)}
                          disabled={aiBusy}
                          title={t.hint}
                          className={`px-2 py-0.5 text-[10px] font-semibold rounded border transition-colors ${aiTone === t.key && aiAlternates.length ? 'bg-purple-600 text-white border-purple-600' : 'bg-white text-purple-700 border-purple-300 hover:bg-purple-100'} disabled:opacity-50`}
                        >
                          {aiBusy && aiTone === t.key ? 'Thinking…' : t.label}
                        </button>
                      ))}
                    </div>
                    {aiAlternates.length > 0 && (
                      <div className="space-y-2 mt-2" data-testid="ai-alternates">
                        {aiAlternates.map((alt, i) => alt ? (
                          <div key={i} className="p-2 bg-white border border-purple-200 rounded text-xs">
                            <div className="text-[10px] font-bold text-purple-500 mb-0.5">Alternate {i + 1}</div>
                            <p className="text-slate-800 leading-relaxed">{alt}</p>
                            <div className="flex gap-1 mt-1.5">
                              <button
                                data-testid={`accept-alternate-${i}`}
                                onClick={() => { setDraft(alt); setAiAlternates([]); }}
                                className="text-[10px] font-semibold px-2 py-0.5 bg-purple-600 text-white rounded hover:bg-purple-700"
                              >
                                Accept
                              </button>
                              <button
                                onClick={() => { setDraft(alt); }}
                                className="text-[10px] font-semibold px-2 py-0.5 text-purple-700 border border-purple-300 rounded hover:bg-purple-100"
                              >
                                Modify
                              </button>
                            </div>
                          </div>
                        ) : null)}
                      </div>
                    )}
                  </div>
                </>
              ) : (
                <>
                  {/* iter296c · Editable TITLE text for the estimate row
                      LABEL cell. Hidden for `value_only` cells (which
                      only manage formatting for the $ figure). Writes
                      via /script-audit/template/estimate-row-title —
                      variant-scoped so all 6 roof types share the label. */}
                  {!activeCell.value_only && activeCell.row_key && (
                    <>
                      <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500 flex items-center gap-1">
                        Title text
                        <span className="text-[9px] italic font-normal text-slate-400">(applies to all 6 roof types)</span>
                      </label>
                      <input
                        type="text"
                        data-testid="estimate-title-input"
                        defaultValue={activeCell.title_text || activeCell.label || ''}
                        onBlur={async (e) => {
                          const val = (e.target.value || '').trim();
                          try {
                            await api.put('/script-audit/template/estimate-row-title', {
                              variant, row_key: activeCell.row_key, title: val,
                            });
                            setPdfBust(b => b + 1);
                            loadCells();
                          } catch (err) {
                            alert('Title save failed: ' + (err?.response?.data?.detail || err.message));
                          }
                        }}
                        placeholder={`Default: ${activeCell.label || 'Estimation'}`}
                        className="w-full mt-1 mb-3 px-3 py-1.5 text-xs border border-[#E2E2DF] rounded-md focus:border-[#316536] focus:outline-none"
                      />
                    </>
                  )}
                  {activeCell.value_only ? (
                    <div className="mb-3 p-3 bg-purple-50/60 border border-purple-200 rounded-md" data-testid="value-only-hint">
                      <div className="text-[11px] text-purple-800">
                        <b>Value cell</b> — format the <code className="px-1 bg-white rounded">$4,200</code> figure independently
                        from the label. Use the <b>Aa formatting toolbar</b> below (font · size · colour · B / I / U).
                        The dollar amount itself is derived from the rate — edit the rate on the sibling
                        <b> {activeCell.title_text || 'Estimation'}</b> cell.
                      </div>
                    </div>
                  ) : (
                    <>
                  {/* Estimate row calc trace — iter297j uses `activeCell.trace`
                      (populated per-row on backend) so reroof shows its
                      correct $/m² from reroof_rate_card, not the empty
                      row.rate. Falls back to legacy rateDraft for
                      any row without trace payload. */}
                  {(() => {
                    const trace = activeCell.trace || {};
                    const rateVal   = trace.rate != null ? trace.rate : (rateDraft.rate ?? 0);
                    const qtySrc    = trace.quantity_source || rateDraft.qty_source || '—';
                    const sourceLbl = trace.source_label;
                    const editable  = trace.editable !== false;
                    const rowKey    = activeCell.row_key || '';
                    return (
                      <>
                        <div className="mb-3 p-3 bg-slate-50 border border-slate-200 rounded-md" data-testid="calc-trace">
                          <div className="flex items-center gap-1.5 mb-2">
                            <Calculator size={11} className="text-slate-500" />
                            <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Calculation trace</span>
                            {sourceLbl && (
                              <span data-testid="rate-source-badge" className="ml-auto text-[9px] px-1.5 py-0.5 bg-indigo-100 text-indigo-800 font-bold rounded uppercase">
                                {sourceLbl}
                              </span>
                            )}
                          </div>
                          <div className="text-[11px] font-mono text-slate-800">
                            Rate: <b>${(typeof rateVal === 'number' ? rateVal.toFixed(2) : rateVal)}</b> per {qtySrc === 'roof_m2' ? 'm²' : qtySrc === 'ridge_length_m' ? 'ridge m' : qtySrc === 'perimeter_length_m' ? 'perimeter m' : qtySrc === 'barge_length_m' ? 'barge m' : (qtySrc || '—')}
                          </div>
                          <div className="text-[11px] font-mono text-slate-800 mt-0.5">
                            Qty source: <b>{qtySrc}</b>
                          </div>
                          {trace.rate_config === 'reroof_rate_card' && (
                            <div className="text-[9px] text-slate-500 italic mt-1">
                              Stored in reroof_rate_card. Editing here updates the same rate used by Report Setup → Roof Rates.
                            </div>
                          )}
                          {trace.rate_config === 'estimate_row_variant_wide' && (
                            <div className="text-[9px] text-slate-500 italic mt-1">
                              Applies across all 3 variants for this roof_key.
                            </div>
                          )}
                          {isEditingDraft && (
                            <div className="text-[9px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5 mt-1.5 inline-block">
                              ⚠️ Rate changes save globally, not to this draft.
                            </div>
                          )}
                        </div>
                        {editable && rowKey && (
                          <>
                            <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500 flex items-center gap-1">
                              Rate ($/{qtySrc === 'ridge_length_m' ? 'ridge m' : qtySrc === 'perimeter_length_m' ? 'perimeter m' : qtySrc === 'barge_length_m' ? 'barge m' : 'm²'})
                            </label>
                            <input
                              type="number"
                              step="0.01"
                              data-testid="rate-input"
                              defaultValue={rateVal}
                              onBlur={async (e) => {
                                const newRate = parseFloat(e.target.value || 0);
                                if (isNaN(newRate) || Math.abs(newRate - rateVal) < 0.001) return;
                                try {
                                  await api.put('/script-audit/estimate-row-rate', {
                                    variant, roof_key: roofKey, row_key: rowKey, rate: newRate,
                                  });
                                  setPdfBust(b => b + 1);
                                  loadCells();
                                } catch (err) {
                                  alert('Rate save failed: ' + (err?.response?.data?.detail || err.message));
                                }
                              }}
                              className="w-full mt-1 mb-2 px-3 py-1.5 text-xs border border-[#E2E2DF] rounded-md focus:border-[#316536] focus:outline-none font-mono"
                            />
                            {(rowKey === 'repoint' || rowKey === 'bedpoint') && (
                              <>
                                <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Quantity source</label>
                                <select
                                  data-testid="qty-source-select"
                                  defaultValue={qtySrc}
                                  onChange={async (e) => {
                                    try {
                                      await api.put('/script-audit/estimate-row-rate', {
                                        variant, roof_key: roofKey, row_key: rowKey,
                                        rate: rateVal, quantity_source: e.target.value,
                                      });
                                      setPdfBust(b => b + 1);
                                      loadCells();
                                    } catch (err) {
                                      alert('Qty source save failed: ' + (err?.response?.data?.detail || err.message));
                                    }
                                  }}
                                  className="w-full mt-1 mb-2 px-3 py-1.5 text-xs border border-[#E2E2DF] rounded-md focus:border-[#316536] focus:outline-none font-mono"
                                >
                                  <option value="ridge_length_m">Ridge length (m)</option>
                                  <option value="perimeter_length_m">Perimeter length (m)</option>
                                  <option value="barge_length_m">Barge length (m)</option>
                                  <option value="roof_m2">Roof area (m²)</option>
                                </select>
                              </>
                            )}
                          </>
                        )}
                      </>
                    );
                  })()}
                  <div className="hidden" data-testid="legacy-rate-drawer">
                  <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500 flex items-center gap-1">
                    Rate
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    data-testid="rate-input-legacy"
                    value={rateDraft.rate ?? ''}
                    onChange={e => setRateDraft(prev => ({ ...prev, rate: parseFloat(e.target.value || 0) }))}
                    onBlur={saveCell}
                    className="w-full mt-1 px-3 py-1.5 text-xs border border-[#E2E2DF] rounded-md focus:border-[#316536] focus:outline-none font-mono"
                  />
                  <div className="flex gap-1.5 mt-2">
                    <button data-testid="save-rate-btn" onClick={saveCell} className="flex items-center gap-1.5 px-2.5 py-1.5 text-[11px] font-semibold bg-[#D35400] text-white hover:bg-[#A04000] rounded-md transition-colors">
                      <Save size={11} /> {saveState === 'saving' ? 'Saving…' : saveState === 'saved' ? 'Saved ✓' : 'Save'}
                    </button>
                    <button data-testid="verify-rate-btn" onClick={verifyCell} className="flex items-center gap-1.5 px-2.5 py-1.5 text-[11px] font-semibold text-emerald-700 border border-emerald-300 hover:bg-emerald-50 rounded-md transition-colors">
                      <CheckCircle2 size={11} /> Verify
                    </button>
                  </div>
                  </div>{/* /legacy-rate-drawer */}
                    </>
                  )}

                  {/* iter296d · Font formatting toolbar for estimate_row
                      cells. Applies to the LABEL font when the Title cell
                      is active, or the VALUE font when the Value cell is
                      active — the FormattingToolbar reads from
                      `activeFormatting` which is keyed by cell_type+cell_key
                      via `_cell_formatting.estimate_row_{idx}[_value]`. */}
                  <FormattingToolbar
                    value={activeFormatting}
                    fonts={fonts}
                    onChange={applyFormattingPatch}
                    onReset={resetFormatting}
                    selectedCount={selectedCells.size}
                    onBulkApply={bulkApplyFormatting}
                    bulletMode={false}
                  />
                </>
              )}
            </>
          )}
        </div>
        </>)}
      </div>
      {/* iter298d · Draft Compare modal — full-screen side-by-side PDF
          comparison of any two Design Drafts on the current variant. */}
      {showCompareModal && refAppraisalId && (
        <DraftCompareModal
          drafts={designDrafts}
          defaultA={designDrafts[0] && designDrafts[0]._id}
          defaultB={designDrafts[1] && designDrafts[1]._id}
          buildUrl={buildComparePreviewUrl}
          variantLabel={`${variant.replace(/_/g, ' ')} · ${roofKey.replace(/_/g, ' ')}`}
          onClose={() => setShowCompareModal(false)}
        />
      )}

      {/* ── Iter288 3C · Focused Preview overlay ────────────────────────
          Full-height panel on the right (slides in). Loads the whole PDF
          plus a section-chip strip along the top so the operator can:
            • Click a chip → jump the audit picker to that section's cell
            • Scroll the PDF freely to any page (browser viewer nav)
          The chip bar always lists the current variant's full section
          order (not page-scoped) — accurate everywhere, no page-tracking
          hacks. Chip for the active cell is highlighted. */}
      {showFocusedPreview && refAppraisalId && (
        <div data-testid="focused-preview-panel" className="fixed top-0 right-0 h-full w-[46vw] bg-white border-l border-[#E2E2DF] shadow-2xl z-50 flex flex-col">
          <div className="px-4 py-3 border-b border-[#E2E2DF] bg-purple-50/60 flex items-center gap-2">
            <span className="text-xs font-bold text-purple-800 uppercase tracking-wider">🔍 Focused Preview</span>
            <span className="text-[10px] text-slate-500 truncate">{variant.replace('_', ' ')} · {roofKey.replace('_', ' ')}</span>
            <button onClick={() => setShowFocusedPreview(false)} className="ml-auto text-slate-400 hover:text-rose-600 text-lg" data-testid="close-focused-preview">✕</button>
          </div>
          {/* Section chip bar — whole-report index (Option b in scope discussion) */}
          <div className="px-3 py-2 border-b border-[#E2E2DF] bg-slate-50 overflow-x-auto">
            <div className="flex flex-nowrap gap-1" data-testid="section-chip-bar">
              {cells.filter(c => c.cell_type === 'template_field').map(c => (
                <button
                  key={c.cell_key}
                  data-testid={`section-chip-${c.cell_key}`}
                  onClick={() => {
                    const i = cells.findIndex(x => x.cell_key === c.cell_key && x.cell_type === 'template_field');
                    if (i >= 0) setActiveIdx(i);
                  }}
                  className={`text-[10px] px-2 py-0.5 rounded whitespace-nowrap border transition-colors ${activeCell && activeCell.cell_key === c.cell_key ? 'bg-purple-600 text-white border-purple-600' : 'bg-white text-purple-700 border-purple-200 hover:bg-purple-100'}`}
                  title={c.label}
                >
                  {activeCell && activeCell.cell_key === c.cell_key ? '⚡ ' : ''}{c.label}
                </button>
              ))}
            </div>
          </div>
          <iframe title="Focused PDF" src={pdfPreviewUrl} className="flex-1 w-full" data-testid="focused-preview-iframe" />
        </div>
      )}

      {/* iter296 · Custom Illustration Layout modal — MVP builder.
          Global scope: created layouts appear for every variant. Anchor
          section keys come from `reorderableSectionKeys` (the same list
          the section-reorder UI uses) so the two features share a single
          vocabulary. Column widths (mm) are optional overrides. */}
      {showCustomLayoutModal && (
        <div
          data-testid="custom-layout-modal"
          className="fixed inset-0 bg-black/40 z-[60] flex items-center justify-center p-4"
          onClick={(e) => { if (e.target === e.currentTarget) setShowCustomLayoutModal(false); }}
        >
          <CustomLayoutBuilder
            reorderableSectionKeys={reorderableSectionKeys}
            sectionLabelFor={sk => (SECTION_LABELS[sk] || sk.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()))}
            onClose={() => setShowCustomLayoutModal(false)}
            onCreate={async (payload) => {
              const created = await createCustomLayout(payload);
              if (created) {
                setShowCustomLayoutModal(false);
                // Auto-apply the newly-created layout so the operator sees the result.
                await applyIllustrationLayout(created.id);
              }
            }}
          />
        </div>
      )}
    </div>
  );
}

// ── iter296 · Custom Illustration Layout builder (MVP) ─────────────────
// Small, focused modal — 1 name field + section-key checkboxes + 2 mm
// inputs. Kept as a plain-function component (no ceremony) so the parent
// page's state doesn't grow further. The parent handles create + apply.
const CustomLayoutBuilder = ({ reorderableSectionKeys, sectionLabelFor, onClose, onCreate }) => {
  const [name, setName] = React.useState('');
  const [anchors, setAnchors] = React.useState([]);
  const [leftMm, setLeftMm] = React.useState(110);
  const [rightMm, setRightMm] = React.useState(55);
  const toggle = (sk) => setAnchors(a => a.includes(sk) ? a.filter(x => x !== sk) : [...a, sk]);
  const canSave = name.trim().length > 0 && anchors.length > 0;
  return (
    <div className="bg-white rounded-md shadow-xl w-full max-w-lg p-4" onClick={e => e.stopPropagation()}>
      <div className="flex items-center gap-2 mb-3">
        <span className="text-sm font-bold text-slate-800">New illustration layout</span>
        <button data-testid="custom-layout-close" onClick={onClose} className="ml-auto text-slate-400 hover:text-rose-600 text-lg">✕</button>
      </div>
      <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-500">Name</label>
      <input
        data-testid="custom-layout-name"
        type="text"
        value={name}
        onChange={e => setName(e.target.value)}
        placeholder="e.g. Client A — hero"
        className="w-full mt-1 px-2 py-1 text-xs border border-slate-300 rounded"
      />
      <div className="mt-3">
        <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">Anchor section(s)</div>
        <div className="grid grid-cols-2 gap-1 max-h-52 overflow-auto p-2 border border-slate-200 rounded">
          {reorderableSectionKeys.map(sk => (
            <label key={sk} className="flex items-center gap-1 text-[11px] cursor-pointer">
              <input
                type="checkbox"
                data-testid={`custom-layout-anchor-${sk}`}
                checked={anchors.includes(sk)}
                onChange={() => toggle(sk)}
              />
              <span className="truncate">{sectionLabelFor(sk)}</span>
            </label>
          ))}
        </div>
      </div>
      <div className="mt-3 flex items-center gap-3 text-[10px]">
        <label className="flex items-center gap-1">
          <span className="text-slate-600 w-16">Left col mm</span>
          <input data-testid="custom-layout-left" type="number" min="40" max="150" value={leftMm} onChange={e => setLeftMm(Number(e.target.value)||0)} className="w-14 px-1 py-0.5 border border-slate-300 rounded text-center" />
        </label>
        <label className="flex items-center gap-1">
          <span className="text-slate-600 w-16">Right col mm</span>
          <input data-testid="custom-layout-right" type="number" min="30" max="90" value={rightMm} onChange={e => setRightMm(Number(e.target.value)||0)} className="w-14 px-1 py-0.5 border border-slate-300 rounded text-center" />
        </label>
      </div>
      <div className="mt-4 flex items-center gap-2 justify-end">
        <button data-testid="custom-layout-cancel" onClick={onClose} className="text-[11px] px-3 py-1 border border-slate-300 rounded text-slate-600 hover:bg-slate-100">Cancel</button>
        <button
          data-testid="custom-layout-save"
          disabled={!canSave}
          onClick={() => onCreate({ name: name.trim(), anchor_section_keys: anchors, left_col_mm: leftMm, right_col_mm: rightMm })}
          className={`text-[11px] px-3 py-1 rounded font-semibold ${canSave ? 'bg-[#316536] text-white hover:bg-[#254a29]' : 'bg-slate-200 text-slate-400 cursor-not-allowed'}`}
        >
          Save & Apply
        </button>
      </div>
    </div>
  );
};
